// =============================================================================
// Renderer.ts — Renderização PixiJS com pixel art: interpolação de entidades,
// predição do próprio jogador, partículas, screen shake, sombras e a câmera
// na Praça dos Três Poderes. Snapshots a 12.5Hz viram 60fps de caos bonito.
//
// PIXI é um global (carregado via <script> em /vendor); os tipos vêm de
// globals.d.ts — os imports de 'pixi.js' abaixo são type-only e somem no bundle.
// =============================================================================
import type { Application, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { INTERP_DELAY_MS, EXTRAP_MAX_MS, ARENA_W, ARENA_H, PLAYER_RADIUS } from '../../shared/constants';
import { PSTATE } from '../../shared/protocol';
import type {
  Snapshot, PlayerSnap, EnemySnap, AllySnap, ProjSnap, GemSnap, CoinSnap,
  ChestSnap, ZoneSnap, GameEvent, FxEvent, GameStartPayload, GameStartPlayer,
} from '../../shared/protocol';
import { CHARACTERS, CHARACTER_TABLE } from '../../shared/characters';
import { EVOLVED_WEAPONS, WEAPONS } from '../../shared/weapons';
import { ETYPE_IDS, typeDef } from '../../shared/enemies';
import { DEFAULT_SKIN } from '../../shared/skins';
import { spriteFactory, skinSpriteId, ENEMY_SPRITE, PROJ_SPRITE } from './sprites';
import { charSheet } from './charSheets';
import type { SheetDir } from './charSheets';
import { fxTex, fxFrames } from './fxSprites';
import { audio } from './AudioEngine';
import {
  ELITE_RING_COLOR, ELITE_RING_TEX_RADIUS, ENEMY_HP_BG_COLOR, ENEMY_HP_FILL_COLOR,
  eliteRingScale, isEliteFlag, isFrozenFlag, isGhostFlag, layoutEnemyHpBar,
} from './enemyHud';
import {
  BURSTS_PER_FRAME, DMG_FLOAT_CAP, KILL_SFX_PER_FRAME, PARTICLE_CAP,
  deathBurstPartCount, deathFxOnScreen, dmgFloatAllowed,
} from './jankGuards';

const ZONE_COLORS: Record<string, number> = {
  panela: 0xff8c42, holofote: 0xffe14d, pf: 0xd42a2a, lava: 0x4aa3df, cachaca: 0xe8b13d,
  cadeira: 0xb0692f,
  onda: 0xffffff,     // onda de knockback (estouro com empurrão)
  pao: 0xe07a8a,      // poça de mortadela (#121)
  raiz: 0x5a8f3c,     // raízes da Amazônia (#121)
  arvore: 0x1e9e4e,   // árvore-barreira (#121)
  tornado: 0xcfe8f4,  // tornado errante / mini-tornado (#121)
  papel: 0xdde7f0,    // papéis cortantes da carteira (#121)
  sombra: 0x6b2d8f,   // explosão de sombra dos morceguinhos (#121)
};
const FRIENDLY_ZONES = new Set<string>([
  'lava', 'cachaca', 'cadeira', 'onda', 'pao', 'raiz', 'arvore', 'tornado', 'papel',
]);  // zonas dos jogadores
// Zonas de chão que ganharam sprite desenhado (spells.png): o desenho substitui
// o círculo chapado. tornado errante reaproveita o sprite da rajada de vento.
const ZONE_FX: Record<string, string> = {
  raiz: 'raiz', arvore: 'raiz', tornado: 'vento',
};
// Sprite sobreposto no cone, por arma (o fx de cone não distingue senão pelo id):
// soluço do Bolsonaro (e a evolução) e o maço do Mensalão.
const CONE_FX: Record<string, string> = {
  live_varanda: 'soluco', cadeia_nacional: 'soluco', mensalao: 'mensalao',
};
// Inimigos com sprite desenhado (atlas em fxSprites): a urna anima em ciclo de
// caminhada; os bosses (issue #116) são retratos estáticos de 1 frame —
// mapeamento arte -> boss documentado na issue, trocar é editar aqui
const ENEMY_DRAWN: Record<string, string> = {
  urna_possuida: 'urna_monster',
  live_19h: 'boss_live_19h',
  mensalao2: 'boss_mensalao2',
  impeachment_dilma: 'boss_impeachment_dilma',
  cpi_covid: 'boss_cpi_covid',
  stf_bolso: 'boss_stf_bolso',
  orcamento_secreto: 'boss_orcamento_secreto',
  lula_planalto: 'boss_lula_planalto',
  oito_jan_invasao: 'boss_oito_jan_invasao',
};
// Escala do sprite desenhado (spells.png) por projétil; 0.9 é o padrão. A
// mãozinha do "Faz o L" pediu pra ser MAIOR no efeito.
const PROJ_FX_SCALE: Record<string, number> = { faz_l: 1.4 };
const SPIN_PROJ = new Set<string>(['gavel', 'pan', 'cachaca', 'cadeira', 'carteira', 'pao', 'faz_l']); // projéteis que giram (bumerangue incluso)
const AIM_PROJ = new Set<string>(['moto', 'dove', 'fake', 'vampiro', 'vento']); // projéteis que apontam pro alvo

// Camadas em ordem de desenho
const LAYER_NAMES = ['bg', 'zones', 'shadows', 'chests', 'gems', 'allies', 'enemies', 'projs', 'players', 'fx', 'quotes'] as const;
type LayerName = (typeof LAYER_NAMES)[number];

type PoolKind = 'enemies' | 'allies' | 'projs' | 'gems' | 'coins' | 'chests';

// -----------------------------------------------------------------------------
// Views tipadas: o JS antigo pendurava campos ad-hoc nos objetos PIXI
// (`sp._body`, `sp._shadow`...). Aqui viram interfaces que estendem Container/
// Sprite, com um cast único no momento da criação de cada sprite.
// -----------------------------------------------------------------------------
interface EntityView extends Container {
  _shadow?: Sprite;
  _shadowDy?: number;
  _cor?: number;       // usado no death burst dos inimigos
  _isBoss?: boolean;
  _poolKey?: string;   // free-list de reciclagem (kind:subtipo)
}

interface EnemyView extends EntityView {
  _body: Sprite;
  _baseScale: number;
  _cor: number;
  _isBoss: boolean;
  _hpF: number;
  _pop: number;
  // HUD em sprites de textura compartilhada (issue perf horda): sem Graphics
  // clear/drawRect/drawCircle no hot path — só scale/position/visible.
  _eliteRing?: Sprite;
  _hpBarBg?: Sprite;
  _hpBarFill?: Sprite;
  _hpBarF: number;     // último hpFrac aplicado na barrinha (_hpF detecta dano)
  _hpBarY: number;     // último y da barrinha (acompanha o pop de escala)
  _hpBarW: number;     // última largura (boss/elite/normal)
  _frozen?: boolean;   // último estado de freeze (dirty tint/frame)
  _elite?: boolean;    // último estado de elite (dirty ring)
  _walkFrame: number;  // último índice de frame de caminhada (-1 = unset)
  _drawn?: string;     // nome do atlas em fxSprites (inimigo com sprite desenhado)
  _radius: number;     // raio do def: recalcula a escala ao trocar pro atlas
  _nome?: Text;        // placa de nome do boss: reposiciona ao trocar pro retrato
}

interface ProjView extends Sprite {
  _kind: string;
}

interface PuddleView extends Sprite {
  _born: number;   // performance.now() do pouso: cronometra os frames do estouro
}

interface ZoneFxView extends Sprite {
  _born: number;   // performance.now() do brote: cronometra a animação de surgir
  _kind: string;   // raiz cresce do chão; tornado gira — a animação depende disso
}

interface PlayerView extends Container {
  _body: Sprite;
  _hp: Graphics;
  _status: Graphics;
  _label: Text;
  _shadow?: Sprite;
  // Animação por sprite sheet (personagens com atlas em charSheets.ts)
  _charId: string;
  _skinId: string;     // skin escolhida no lobby (issue #24)
  _sheetOn: boolean;   // atlas carregado e anchor/scale já ajustados
  _sheetDir: SheetDir; // última direção olhada (mantida quando para)
  _facing: number;     // 1 = direita, -1 = esquerda (só afeta o perfil)
  _animT: number;      // relógio do ciclo de caminhada
  _hpFrac: number;     // último hpFrac desenhado na barrinha
  _statusKey: string;  // último estado desenhado no anel (st|rev|inv|frz)
}

// Efeitos com ttl (cones, ondas, quotes). `draw` é declarado como
// método (bivariante) pra aceitar callbacks que assumem `g` presente.
interface Transient {
  ttl: number;
  max: number;
  g?: Graphics;
  text?: Text;
  sp?: Sprite;   // efeito instantâneo desenhado (soluço/8jan/STF/PIX)
  draw(g: Graphics | undefined, k: number, dt: number): void;
}

// Partícula do death burst: sprite branco tintado, reciclado num pool
interface Particle {
  sp: Sprite;
  vx: number;
  vy: number;
  ttl: number;
}

// Número de dano flutuante (RPG): PIXI.Text reciclado subindo até sumir
interface DmgFloat {
  sp: Text;
  ttl: number;
}

interface QuoteSpawn {
  text: string;
  x: number;
  y: number;
  boss?: boolean;
}

// PRNG determinístico pra espalhar os props do cenário
function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Renderer {
  private readonly app: Application<HTMLCanvasElement>;
  private readonly world: Container;
  private readonly layers: Record<LayerName, Container>;
  private readonly zonesG: Graphics;
  private readonly vignette: Sprite;
  private readonly sprites: { players: Map<string, PlayerView> } & Record<PoolKind, Map<number, EntityView>>;
  private readonly transients: Transient[] = []; // efeitos com ttl (cones, ondas, quotes)
  // Free-lists de views por kind:subtipo — reciclar evita o churn de
  // destroy/create no cap de inimigos + fronteira do AOI (hitch de GC)
  private readonly freeViews = new Map<string, EntityView[]>();
  private static readonly FREE_CAP = 64;   // além disso, destrói de verdade
  // Partículas do death burst (pool compartilhado; ver spawnDeathBurst)
  private readonly particleLayer: Container;
  private readonly particles: Particle[] = [];
  private readonly particleFree: Sprite[] = [];
  private static readonly PARTICLE_TTL = 0.45;
  // Teto baixo de propósito: wipe de horda + easter egg alocava 600*sprites e
  // virava hitch de GC (trace: heap 20↔200MB). Ver jankGuards.ts.
  private static readonly PARTICLE_CAP = PARTICLE_CAP;
  // Dano flutuante: hp dos inimigos comparado entre snapshots, ticks de dps
  // agregados por inimigo e soltos como número pixelado subindo
  private readonly dmgFloats: DmgFloat[] = [];
  private readonly dmgFloatFree: Text[] = [];
  private readonly dmgAcc = new Map<number, { acc: number; since: number; x: number; y: number }>();
  private readonly enemyHp = new Map<number, number>();
  private readonly dmgSeen = new Set<number>();
  private lastDmgSnap: Snapshot | null = null;
  private static readonly DMG_TTL = 0.8;
  private static readonly DMG_CAP = DMG_FLOAT_CAP;
  private static readonly DMG_FLUSH_MS = 300;  // janela de agregação de dps
  // Orçamentos por frame: wipe de 800 kills não spawna 800 bursts + 800 sfx
  private burstBudget = BURSTS_PER_FRAME;
  private killSfxBudget = KILL_SFX_PER_FRAME;
  // Scratch reutilizado no hot path: alocar Map/Set novos a 60fps × 5 pools
  // gerava dezenas de milhares de objetos curtos por segundo (pausas de GC)
  private readonly scratchMapA = new Map<number, unknown>();
  private readonly scratchSeen = new Set<number>();
  private readonly playersMapA = new Map<string, PlayerSnap>();
  private readonly playersSeen = new Set<string>();
  private lastZonesRef: ZoneSnap[] | null = null; // zonas só mudam por snapshot
  // Cenário estático desenhado uma vez em buildBackground (ver ordem lá)
  private propShadowG!: Graphics; // sombras elípticas sob props e monumentos
  private petalG!: Graphics;      // pétalas de ipê caídas
  private waterFxG!: Graphics;    // reflexos do espelho d'água (animados)
  private bgTime = 0;             // relógio dos efeitos ambientes

  // Resolução dinâmica (issue #102): fill rate é o gargalo clássico de GPU
  // integrada em tela retina/4K. FPS afundou de forma sustentada? Desce um
  // degrau de resolução e deixa o CSS esticar (pixel art não se ofende).
  // Volta a subir só com folga longa — histerese pra não ficar oscilando.
  private static readonly RES_STEPS = [1, 1.25, 1.5, 2];
  private static readonly RES_DOWN_FPS = 50;   // abaixo disso, conta pra descer
  private static readonly RES_UP_FPS = 58;     // acima disso, conta pra subir
  private static readonly RES_DOWN_AFTER = 2;  // s sustentados pra descer
  private static readonly RES_UP_AFTER = 12;   // s sustentados pra subir
  private static readonly RES_COOLDOWN = 5;    // s entre trocas de degrau
  private resIndex: number;      // degrau atual em RES_STEPS
  private resMaxIndex: number;   // teto do aparelho (DPR na criação)
  private resLowT = 0;
  private resHighT = 0;
  private resCooldown = 0;

  // Auras contínuas como SPRITES com texturas pré-renderizadas: o Graphics
  // anterior fazia clear+redraw por frame (re-tesselação na CPU, issue #102)
  private auraDotTex!: Texture;
  private auraCruzTex!: Texture;
  private cruzTrailTex!: Texture;
  private icmsCoinTex!: Texture;
  private icmsRingTex!: Texture;
  // Sombra elíptica como SPRITE de textura compartilhada (issue #145): o
  // Graphics anterior era um por entidade e quebrava o batch (draw call por
  // sombra). Agora todas as sombras da camada batcham numa textura só.
  private shadowTex!: Texture;
  private static readonly SHADOW_BASE = 64;  // meia-largura da elipse na textura
  // HUD de inimigo em texturas compartilhadas (perf horda): barra bg/fill +
  // anel de elite — o Graphics por entidade virava re-tesselação + batch break.
  private hpBarBgTex!: Texture;
  private hpBarFillTex!: Texture;
  private eliteRingTex!: Texture;
  private readonly auraPool: Sprite[] = [];
  private auraUsed = 0;
  private static readonly CRUZ_SPRITE_SCALE = 1.7; // legibilidade em telas menores
  private static readonly ICMS_RING_R = 125;  // raio-base da textura do campo
  private static readonly ICMS_DRAIN_TARGETS = 6; // teto visual, independente do DPS

  // Texturas de canvas criadas por ESTE Renderer (tiles, vinheta, auras):
  // registradas pra morrer junto no destroy(). As texturas compartilhadas dos
  // caches de módulo (charSheets/spriteFactory/fxSprites) NÃO entram aqui —
  // o Renderer seguinte da REELEIÇÃO as reutiliza (issue #135).
  private readonly ownTextures: Texture[] = [];

  private buffer: { at: number; snap: Snapshot }[] = []; // snapshots recebidos [{at, snap}]
  private youId: string | null = null;
  private readonly me = { x: ARENA_W / 2, y: ARENA_H / 2 };  // posição PREDITA local
  private myServer: PlayerSnap | null = null;
  private paused = false;             // urna aberta: mundo congelado
  private shake = 0;                  // screen shake decaindo
  // Retângulo de culling em coordenadas de mundo (issue #145): o servidor manda
  // até AOI_RADIUS (1700), bem mais do que cabe na tela. Entidade fora daqui
  // fica com visible=false — segue no buffer de snapshot, então volta a
  // aparecer interpolada sem pop-in. Recalculado por frame a partir da câmera.
  private cullMinX = -Infinity;
  private cullMaxX = Infinity;
  private cullMinY = -Infinity;
  private cullMaxY = Infinity;
  // Margem além da borda pra sprites grandes (boss) não sumirem meio na tela
  private static readonly CULL_MARGIN = 320;
  private getInput: () => { mx: number; my: number } = () => ({ mx: 0, my: 0 });
  private running = false;
  private playersInfo = new Map<string, GameStartPlayer>();

  constructor(mount: HTMLElement) {
    this.app = new PIXI.Application<HTMLCanvasElement>({
      resizeTo: window, background: 0x121218, antialias: false,
      resolution: Math.min(window.devicePixelRatio || 1, 2), autoDensity: true,
      // Notebook com GPU dupla escolhe a INTEGRADA por padrão pra WebGL;
      // isto pede a dedicada — ganho grande nas máquinas que mais engasgam
      powerPreference: 'high-performance',
    });
    // Acessibilidade do Pixi percorre o grafo no postrender quando ativa (Tab)
    // e apareceu no profile da horda. O jogo é canvas+teclas; desliga o plugin.
    this.disablePixiAccessibility();
    // Hit-test de pointermove na árvore inteira (hitTestMoveRecursive) com
    // horda de sprites = input "molenga". Canvas do jogo não precisa de pick.
    this.disablePixiInteraction();
    mount.appendChild(this.app.view);
    // Início na resolução mais alta que os degraus permitem pro DPR da tela;
    // se o FPS afundar, updateDynamicResolution desce degrau a degrau
    const res0 = this.app.renderer.resolution;
    this.resIndex = 0;
    for (let i = Renderer.RES_STEPS.length - 1; i >= 0; i--) {
      if (Renderer.RES_STEPS[i] <= res0) { this.resIndex = i; break; }
    }
    this.resMaxIndex = this.resIndex;

    this.world = new PIXI.Container();
    this.disableInteractive(this.app.stage);
    this.disableInteractive(this.world);
    this.app.stage.addChild(this.world);

    // Camadas em ordem de desenho
    const layers = {} as Record<LayerName, Container>;
    for (const name of LAYER_NAMES) {
      layers[name] = new PIXI.Container();
      this.disableInteractive(layers[name]);
      this.world.addChild(layers[name]);
    }
    this.layers = layers;
    this.zonesG = new PIXI.Graphics();
    this.layers.zones.addChild(this.zonesG);
    this.makeAuraTextures();
    this.shadowTex = this.makeShadowTexture();
    this.makeEnemyHudTextures();
    this.particleLayer = new PIXI.Container();
    this.layers.fx.addChild(this.particleLayer);

    this.sprites = { players: new Map(), enemies: new Map(), allies: new Map(), projs: new Map(), gems: new Map(), coins: new Map(), chests: new Map() };

    this.buildBackground();
    this.vignette = this.buildVignette();
    // Cap de 60fps: em telas de 120Hz+ o budget por frame dobra, absorvendo
    // picos de frame time (GC, criação de sprites) que causavam stutter
    this.app.ticker.maxFPS = 60;
    this.app.ticker.add(() => this.frame(this.app.ticker.deltaMS / 1000));
  }

  // Textura exclusiva deste Renderer a partir de um canvas 2D — vai pra lista
  // de destruição do destroy() (nunca use pra textura de cache compartilhado)
  private ownTexture(c: HTMLCanvasElement): Texture {
    const tex = new PIXI.Texture(new PIXI.BaseTexture(c, { scaleMode: PIXI.SCALE_MODES.NEAREST }));
    this.ownTextures.push(tex);
    return tex;
  }

  // FPS já suavizado pelo ticker do PixiJS (exibido no canto superior direito)
  get fps(): number {
    return Math.round(this.app.ticker.FPS);
  }

  // Controle da resolução dinâmica: FPS sustentadamente baixo desce um degrau;
  // folga longa sobe de volta (até o teto do aparelho). Cooldown entre trocas.
  private updateDynamicResolution(dt: number): void {
    const fps = this.app.ticker.FPS;
    if (fps < Renderer.RES_DOWN_FPS) { this.resLowT += dt; this.resHighT = 0; }
    else if (fps > Renderer.RES_UP_FPS) { this.resHighT += dt; this.resLowT = 0; }
    else { this.resLowT = 0; this.resHighT = 0; }
    if (this.resCooldown > 0) { this.resCooldown -= dt; return; }
    if (this.resLowT >= Renderer.RES_DOWN_AFTER && this.resIndex > 0) {
      this.applyResolution(this.resIndex - 1);
    } else if (this.resHighT >= Renderer.RES_UP_AFTER && this.resIndex < this.resMaxIndex) {
      this.applyResolution(this.resIndex + 1);
    }
  }

  private applyResolution(idx: number): void {
    this.resIndex = idx;
    this.resLowT = 0;
    this.resHighT = 0;
    this.resCooldown = Renderer.RES_COOLDOWN;
    this.app.renderer.resolution = Renderer.RES_STEPS[idx];
    // Re-aplica o resizeTo com a densidade nova; autoDensity mantém o tamanho
    // CSS e o canvas estica pixelado (estética da casa, ninguém percebe)
    this.app.resize();
  }

  // ---------------------------------------------------------------------------
  // Cenário: asfalto de Brasília, territórios ideológicos, palmeiras, Congresso
  // ---------------------------------------------------------------------------
  private buildBackground(): void {
    // Chão base: gramado do cerrado cobrindo a arena inteira
    this.layers.bg.addChild(new PIXI.TilingSprite(this.makeGrassTile(), ARENA_W, ARENA_H));

    this.buildTerrainDetails();
    this.buildGroundDecals();
    this.buildWater();

    // Cerca da arena (grade de proteção do Congresso)
    const border = new PIXI.Graphics();
    border.lineStyle(12, 0xf7d117, 0.55).drawRect(0, 0, ARENA_W, ARENA_H);
    border.lineStyle(4, 0x1e9e4e, 0.6).drawRect(10, 10, ARENA_W - 20, ARENA_H - 20);
    this.layers.bg.addChild(border);

    // Letreiros e pichações no chão
    const deco = (txt: string, x: number, y: number, size = 60, rot = 0, alpha = 0.05): void => {
      const t = new PIXI.Text(txt, { fontSize: size, fill: 0xffffff, fontWeight: '900' });
      t.alpha = alpha; t.anchor.set(0.5); t.position.set(x, y); t.rotation = rot;
      this.layers.bg.addChild(t);
    };
    deco('PRAÇA DOS TRÊS PODERES', ARENA_W / 2, ARENA_H / 2 - 220, 80);
    deco('ORDEM E (ALGUM) PROGRESSO', ARENA_W / 2, ARENA_H / 2 + 220, 60);
    deco('ZONA ELEITORAL 666', ARENA_W * 0.2, ARENA_H * 0.8, 50);
    deco('FORA TODOS', ARENA_W * 0.3, ARENA_H * 0.12, 40, -0.12, 0.07);
    deco('EU AVISEI', ARENA_W * 0.82, ARENA_H * 0.68, 36, 0.1, 0.07);
    deco('CADÊ A TERCEIRA VIA?', ARENA_W * 0.12, ARENA_H * 0.38, 34, -0.08, 0.07);

    // Santinhos de candidato espalhados (lixo eleitoral pós-comício)
    const rngS = mulberry32(2022);
    const santinhos = ['deco_santinho_a', 'deco_santinho_b', 'deco_santinho_c'];
    for (let i = 0; i < 70; i++) {
      const x = 80 + rngS() * (ARENA_W - 160);
      const y = 80 + rngS() * (ARENA_H - 160);
      const id = santinhos[Math.floor(rngS() * santinhos.length)];
      const rot = rngS() * Math.PI * 2;
      if (Math.hypot(x - ARENA_W / 2, y - ARENA_H / 2) < 320) continue; // spawn limpo
      const sp = new PIXI.Sprite(spriteFactory.tex(id));
      sp.anchor.set(0.5); sp.rotation = rot; sp.scale.set(2);
      sp.alpha = 0.5; sp.position.set(x, y);
      this.layers.bg.addChild(sp);
    }

    // Pétalas e sombras entram ANTES dos props: dentro do container bg a
    // ordem de inserção é a ordem de desenho (chão -> sombra -> prop)
    this.petalG = new PIXI.Graphics();
    this.layers.bg.addChild(this.petalG);
    this.propShadowG = new PIXI.Graphics();
    this.layers.bg.addChild(this.propShadowG);

    this.buildLandmarks();

    // Props em pixel art espalhados deterministicamente (fora do centro)
    const rng = mulberry32(1988); // ano da Constituição, respeite
    const props = [
      'prop_palmeira', 'prop_palmeira', 'prop_ipe', 'prop_ipe', 'prop_arbusto', 'prop_arbusto',
      'prop_poste', 'prop_poste', 'prop_cone', 'prop_cone', 'prop_banco', 'prop_banca',
      'prop_barraca', 'prop_carrinho', 'prop_outdoor',
    ];
    const smallScale: Record<string, number> = { prop_cone: 2.2, prop_arbusto: 2.4, prop_banco: 2.4, prop_poste: 2.6 };
    for (let i = 0; i < 130; i++) {
      const x = 120 + rng() * (ARENA_W - 240);
      const y = 160 + rng() * (ARENA_H - 280);
      const id = props[Math.floor(rng() * props.length)];
      if (Math.hypot(x - ARENA_W / 2, y - ARENA_H / 2) < 540) continue;             // centro limpo
      if (this.landmarkSpots.some((l) => Math.hypot(x - l.x, y - l.y) < 420)) continue; // respeita os palácios
      if (Math.abs(x - ARENA_W / 2) < 500 && Math.abs(y - ARENA_H * 0.26) < 210) continue; // ninguém planta ipê no espelho d'água
      this.addProp(id, x, y, smallScale[id] ?? 3.2);
      if (id === 'prop_ipe') this.scatterPetals(x, y, rng);
    }
  }

  // Avenida = meia-largura do Eixo Monumental (compartilhado com a grama)
  private static readonly AVENUE_HALF = 110;
  private static readonly SIDEWALK_W = 46; // calçada de cada lado da avenida

  // Eixo Monumental: calçadas, asfalto, faixa amarela, travessias, bueiros
  // e marcas de frenagem — o resto do mapa é gramado
  private buildGroundDecals(): void {
    const g = new PIXI.Graphics();
    const cx = ARENA_W / 2, cy = ARENA_H / 2;
    const half = Renderer.AVENUE_HALF;

    // Calçadas de concreto flanqueando as avenidas
    const walk = Renderer.SIDEWALK_W;
    g.beginFill(0x2c2c35)
      .drawRect(0, cy - half - walk, ARENA_W, (half + walk) * 2)
      .drawRect(cx - half - walk, 0, (half + walk) * 2, ARENA_H)
      .endFill();
    // Asfalto
    g.beginFill(0x232329)
      .drawRect(0, cy - half, ARENA_W, half * 2)
      .drawRect(cx - half, 0, half * 2, ARENA_H)
      .endFill();
    // Meio-fio (redesenha o cruzamento por cima pra não cortar o asfalto)
    g.lineStyle(3, 0x3c3c46, 0.9)
      .moveTo(0, cy - half).lineTo(ARENA_W, cy - half)
      .moveTo(0, cy + half).lineTo(ARENA_W, cy + half)
      .moveTo(cx - half, 0).lineTo(cx - half, ARENA_H)
      .moveTo(cx + half, 0).lineTo(cx + half, ARENA_H);
    g.lineStyle(0);
    g.beginFill(0x232329).drawRect(cx - half, cy - half, half * 2, half * 2).endFill();

    // Faixa dupla amarela

    for (let x = 40; x < ARENA_W; x += 130) {
      if (Math.abs(x - cx) < half + 40) continue; // cruzamento sem faixa
      g.beginFill(0x8f7a2a, 0.5).drawRect(x, cy - 4, 70, 3).drawRect(x, cy + 2, 70, 3).endFill();
    }
    for (let y = 40; y < ARENA_H; y += 130) {
      if (Math.abs(y - cy) < half + 40) continue;
      g.beginFill(0x8f7a2a, 0.5).drawRect(cx - 4, y, 3, 70).drawRect(cx + 2, y, 3, 70).endFill();
    }

    // Faixas de pedestre: `at` é a borda inicial do corredor de travessia
    const zebraW = 6 * 26;
    const zebra = (vertical: boolean, at: number): void => {
      for (let i = 0; i < 6; i++) {
        const o = at + i * 26;
        g.beginFill(0xd8d8d8, 0.14);
        if (vertical) g.drawRect(cx - half + 12, o, half * 2 - 24, 14); // avenida vertical: pedestre cruza no eixo x
        else g.drawRect(o, cy - half + 12, 14, half * 2 - 24);
        g.endFill();
      }
    };
    zebra(false, cx - 660); zebra(false, cx + 660 - zebraW);
    zebra(false, 320); zebra(false, ARENA_W - 320 - zebraW);
    zebra(true, cy - 660); zebra(true, cy + 660 - zebraW);
    zebra(true, 320); zebra(true, ARENA_H - 320 - zebraW);

    // Bueiros nas avenidas (drenagem tão eficiente quanto a política)
    const manhole = (x: number, y: number): void => {
      g.beginFill(0x17171d, 0.9).drawCircle(x, y, 18).endFill();
      g.lineStyle(3, 0x3a3a46, 0.9).drawCircle(x, y, 18);
      g.lineStyle(2, 0x2c2c36, 0.9)
        .moveTo(x - 9, y - 4).lineTo(x + 9, y - 4)
        .moveTo(x - 9, y + 4).lineTo(x + 9, y + 4);
      g.lineStyle(0);
    };
    for (let i = 0; i < 6; i++) {
      const t = 420 + i * 620;
      if (Math.abs(t - cx) > 700) manhole(t, cy + (i % 2 ? 52 : -52));
      if (Math.abs(t - cy) > 700) manhole(cx + (i % 2 ? -52 : 52), t);
    }

    // Marcas de frenagem (racha na Esplanada, apuração do Detran pendente)
    const rngT = mulberry32(171);
    g.lineStyle(6, 0x0d0d11, 0.35);
    for (let i = 0; i < 4; i++) {
      const x = 400 + rngT() * (ARENA_W - 800);
      const y = cy - 50 + rngT() * 60;
      const bend = (rngT() - 0.5) * 90;
      if (Math.abs(x - cx) < 760) continue;
      for (const off of [0, 26]) {
        g.moveTo(x, y + off).quadraticCurveTo(x + 130, y + off + bend, x + 260, y + off - 25);
      }
    }
    for (let i = 0; i < 4; i++) {
      const y = 400 + rngT() * (ARENA_H - 800);
      const x = cx - 50 + rngT() * 60;
      const bend = (rngT() - 0.5) * 90;
      if (Math.abs(y - cy) < 760) continue;
      for (const off of [0, 26]) {
        g.moveTo(x + off, y).quadraticCurveTo(x + off + bend, y + 130, x + off - 25, y + 260);
      }
    }
    g.lineStyle(0);

    this.layers.bg.addChild(g);
  }

  // Tile de grama procedural (pixel art com grão de 4px, NEAREST): tons do
  // cerrado, lâminas claras e terra seca aparecendo aqui e ali
  private makeGrassTile(): Texture {
    const size = 128, px = 4;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const ctx = c.getContext('2d')!;
    const rng = mulberry32(1500);
    const tones = ['#1d3a25', '#21422a', '#254a30', '#1a3221'];
    for (let y = 0; y < size; y += px) {
      for (let x = 0; x < size; x += px) {
        ctx.fillStyle = tones[Math.floor(rng() * tones.length)];
        ctx.fillRect(x, y, px, px);
      }
    }
    for (let i = 0; i < 70; i++) { // lâminas de capim mais claras
      const x = Math.floor(rng() * (size / px)) * px, y = Math.floor(rng() * (size / px)) * px;
      ctx.fillStyle = rng() < 0.5 ? '#2f5c3a' : '#377047';
      ctx.fillRect(x, y, px, px + (rng() < 0.3 ? px : 0));
    }
    for (let i = 0; i < 10; i++) { // terra do cerrado aparecendo
      const x = Math.floor(rng() * (size / px)) * px, y = Math.floor(rng() * (size / px)) * px;
      ctx.fillStyle = '#33301f';
      ctx.fillRect(x, y, px * 2, px);
    }
    return this.ownTexture(c);
  }

  // Tile de terra batida do cerrado (mesma pegada do tile de grama)
  private makeDirtTile(): Texture {
    const size = 128, px = 4;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const ctx = c.getContext('2d')!;
    const rng = mulberry32(1930); // revolução de 30: terra arrasada
    const tones = ['#3a2f1f', '#413522', '#332a1b', '#453824'];
    for (let y = 0; y < size; y += px) {
      for (let x = 0; x < size; x += px) {
        ctx.fillStyle = tones[Math.floor(rng() * tones.length)];
        ctx.fillRect(x, y, px, px);
      }
    }
    for (let i = 0; i < 26; i++) { // pedrinhas e torrões
      const x = Math.floor(rng() * (size / px)) * px, y = Math.floor(rng() * (size / px)) * px;
      ctx.fillStyle = rng() < 0.5 ? '#54432a' : '#2a2216';
      ctx.fillRect(x, y, px, px);
    }
    return this.ownTexture(c);
  }

  // Variações do gramado: clareiras de terra batida com borda pixelada
  // (células quantizadas + ruído determinístico), tufos de capim e florzinhas
  // espalhados pelo mapa inteiro
  private buildTerrainDetails(): void {
    const CELL = 26;
    const cx = ARENA_W / 2, cy = ARENA_H / 2;
    const half = Renderer.AVENUE_HALF + Renderer.SIDEWALK_W;
    const lx = cx, ly = ARENA_H * 0.26; // espelho d'água
    const onRoad = (x: number, y: number): boolean =>
      Math.abs(x - cx) < half + 16 || Math.abs(y - cy) < half + 16;
    const onWater = (x: number, y: number): boolean =>
      Math.abs(x - lx) < 480 && Math.abs(y - ly) < 190;
    const noise = (gx: number, gy: number): number => {
      const n = Math.sin(gx * 127.1 + gy * 311.7) * 43758.5453;
      return n - Math.floor(n);
    };

    // Clareiras de terra batida (o cerrado vencendo a zeladoria)
    const dirt = new PIXI.Graphics();
    dirt.beginTextureFill({ texture: this.makeDirtTile() });
    const rng = mulberry32(1891); // primeira Constituição da República
    for (let i = 0; i < 16; i++) {
      const px = 200 + rng() * (ARENA_W - 400);
      const py = 200 + rng() * (ARENA_H - 400);
      const rx = 120 + rng() * 200, ry = rx * (0.5 + rng() * 0.3);
      if (Math.hypot(px - cx, py - cy) < 620) continue; // spawn só grama
      for (let y = py - ry - CELL; y <= py + ry + CELL; y += CELL) {
        for (let x = px - rx - CELL; x <= px + rx + CELL; x += CELL) {
          const qx = Math.round(x / CELL) * CELL, qy = Math.round(y / CELL) * CELL;
          if (onRoad(qx, qy) || onWater(qx, qy)) continue;
          const d = ((qx - px) / rx) ** 2 + ((qy - py) / ry) ** 2;
          const thr = 0.55 + noise(qx / CELL, qy / CELL) * 0.5;
          if (d < thr) dirt.drawRect(qx - CELL / 2, qy - CELL / 2, CELL, CELL);
        }
      }
    }
    dirt.endFill();
    this.layers.bg.addChild(dirt);

    // Tufos de capim e florzinhas por todo o gramado
    const rng2 = mulberry32(1822); // independência ou morte
    const flowers = new PIXI.Graphics();
    for (let i = 0; i < 420; i++) {
      const x = 60 + rng2() * (ARENA_W - 120);
      const y = 80 + rng2() * (ARENA_H - 160);
      const kind = rng2();
      if (onRoad(x, y) || onWater(x, y)) continue;
      if (kind < 0.3) {
        flowers.beginFill(kind < 0.08 ? 0xe8e8dc : 0xe8c93a, 0.85).drawRect(x - 2, y - 2, 5, 5).endFill();
      } else {
        const sp = new PIXI.Sprite(spriteFactory.tex(kind < 0.65 ? 'deco_tufo' : 'deco_tufo2'));
        sp.anchor.set(0.5, 1); sp.scale.set(2.6); sp.position.set(x, y); sp.alpha = 0.95;
        this.layers.bg.addChild(sp);
      }
    }
    this.layers.bg.addChild(flowers);
  }

  // Espelho d'água do Congresso, retangular como manda o modernismo
  // (decorativo: aqui todo mundo anda sobre a água)
  private buildWater(): void {
    const lx = ARENA_W / 2, ly = ARENA_H * 0.26;
    const w = 840, h = 240;
    const g = new PIXI.Graphics();
    g.beginFill(0x2c2c35).drawRect(lx - w / 2 - 26, ly - h / 2 - 26, w + 52, h + 52).endFill(); // borda de concreto
    g.beginFill(0x16293c).drawRect(lx - w / 2, ly - h / 2, w, h).endFill();
    g.beginFill(0x1d3a52, 0.9).drawRect(lx - w / 2 + 30, ly - h / 2 + 22, w - 60, h - 44).endFill();
    g.lineStyle(3, 0x3c3c46, 0.9).drawRect(lx - w / 2, ly - h / 2, w, h); // mureta
    g.lineStyle(0);
    this.layers.bg.addChild(g);
    // Reflexos animados em frame() (alpha pulsa, linhas derivam de leve)
    const fx = new PIXI.Graphics();
    fx.lineStyle(3, 0x4a7fa8, 0.8)
      .moveTo(lx - 220, ly - 20).lineTo(lx - 60, ly - 20)
      .moveTo(lx - 90, ly + 26).lineTo(lx + 130, ly + 26)
      .moveTo(lx + 40, ly - 44).lineTo(lx + 210, ly - 44);
    this.layers.bg.addChild(fx);
    this.waterFxG = fx;
  }

  // Pétalas de ipê caídas ao redor do tronco (o outono chega até em Brasília)
  private scatterPetals(x: number, y: number, rng: () => number): void {
    const tones = [0xe8c93a, 0xc9a812, 0xd4b02a];
    for (let i = 0; i < 12; i++) {
      const a = rng() * Math.PI * 2, r = 14 + rng() * 48;
      this.petalG.beginFill(tones[Math.floor(rng() * tones.length)], 0.8)
        .drawRect(x + Math.cos(a) * r - 2, y + 6 + Math.sin(a) * r * 0.5 - 2, 5, 4).endFill();
    }
  }

  // Monumentos fixos de Brasília (decoração; ninguém colide com o patrimônio)
  private readonly landmarkSpots: { x: number; y: number }[] = [];

  private addProp(id: string, x: number, y: number, scale: number): Sprite {
    const sp = new PIXI.Sprite(spriteFactory.tex(id));
    sp.anchor.set(0.5, 1);
    sp.scale.set(scale);
    sp.position.set(x, y);
    sp.alpha = 0.92;
    // Sombra elíptica na base: ancora o prop no chão (senão parece adesivo)
    const rx = Math.max(sp.width * 0.4, 12);
    this.propShadowG.beginFill(0x000000, 0.22).drawEllipse(x, y - 2, rx, rx * 0.38).endFill();
    this.layers.bg.addChild(sp);
    return sp;
  }

  private buildLandmarks(): void {
    const put = (id: string, fx: number, fy: number, scale: number, palmeiras = true): void => {
      const x = ARENA_W * fx, y = ARENA_H * fy;
      this.landmarkSpots.push({ x, y });
      const sp = this.addProp(id, x, y, scale);
      if (palmeiras) {
        const dx = sp.width / 2 + 70;
        this.addProp('prop_palmeira', x - dx, y + 20, 3.2);
        this.addProp('prop_palmeira', x + dx, y + 20, 3.2);
      }
    };
    put('prop_congresso_nacional', 0.5, 0.14, 10);
    put('prop_planalto', 0.85, 0.5, 9);
    put('prop_stf', 0.15, 0.5, 9);
    put('prop_catedral', 0.5, 0.87, 9);
    put('prop_bandeirao', 0.65, 0.34, 6, false);
  }

  // Vinheta cinematográfica fixa na tela (fora do mundo)
  private buildVignette(): Sprite {
    const c = document.createElement('canvas');
    c.width = 512; c.height = 512;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(256, 256, 170, 256, 256, 380);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.38)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 512, 512);
    const vignette = new PIXI.Sprite(this.ownTexture(c));
    this.app.stage.addChild(vignette);
    return vignette;
  }

  // Texturas das auras contínuas, desenhadas UMA vez em canvas 2D. Por frame
  // só se movem sprites (transform barato, batcheado) — nada de re-tesselar.
  private makeAuraTextures(): void {
    const mk = (w: number, h: number, draw: (g: CanvasRenderingContext2D) => void): Texture => {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      draw(c.getContext('2d')!);
      return this.ownTexture(c);
    };
    // Dose do #FiqueEmCasa (#121): círculo esverdeado de vacina em órbita
    this.auraDotTex = mk(14, 14, (g) => {
      g.fillStyle = 'rgba(46,204,113,0.65)';
      g.beginPath(); g.arc(7, 7, 6, 0, Math.PI * 2); g.fill();
      g.fillStyle = 'rgba(208,236,248,0.8)';
      g.beginPath(); g.arc(7, 7, 3, 0, Math.PI * 2); g.fill();
    });
    // Cruz Credo: auréola + cruz dourada + brilho (mesma geometria do vetor)
    this.auraCruzTex = mk(44, 44, (g) => {
      g.fillStyle = 'rgba(255,233,160,0.18)';
      g.beginPath(); g.arc(22, 22, 17, 0, Math.PI * 2); g.fill();
      g.fillStyle = 'rgba(212,175,55,0.95)';
      g.fillRect(19, 9, 6, 26);   // haste
      g.fillRect(12, 15, 20, 6);  // braço
      g.fillStyle = 'rgba(247,231,176,0.9)';
      g.fillRect(21, 16, 2, 10);  // brilho
    });
    // Rastro curto do rosário: dois pixels de luz marcam o setor angular em
    // que a cruz realmente acerta, sem transformar a órbita num círculo chapado.
    this.cruzTrailTex = mk(8, 8, (g) => {
      g.fillStyle = 'rgba(36,20,92,0.85)';
      g.fillRect(2, 1, 4, 6); g.fillRect(1, 2, 6, 4);
      g.fillStyle = 'rgba(142,69,214,0.9)';
      g.fillRect(2, 2, 4, 4);
      g.fillStyle = 'rgba(255,225,92,1)';
      g.fillRect(3, 3, 2, 2);
    });
    // Moedinha de porcentagem que viaja do inimigo tributado ao jogador.
    // Construída só com retângulos inteiros: continua pixelada mesmo pequena.
    this.icmsCoinTex = mk(16, 16, (g) => {
      g.fillStyle = '#082756';
      g.fillRect(5, 1, 6, 1); g.fillRect(3, 2, 10, 1);
      g.fillRect(2, 3, 12, 2); g.fillRect(1, 5, 14, 6);
      g.fillRect(2, 11, 12, 2); g.fillRect(3, 13, 10, 1);
      g.fillRect(5, 14, 6, 1);
      g.fillStyle = '#f3a712';
      g.fillRect(4, 3, 8, 1); g.fillRect(3, 4, 10, 2);
      g.fillRect(2, 6, 12, 4); g.fillRect(3, 10, 10, 2);
      g.fillRect(4, 12, 8, 1);
      g.fillStyle = '#ffe45e';
      g.fillRect(4, 4, 6, 1); g.fillRect(3, 5, 2, 4);
      g.fillStyle = '#713800';
      g.fillRect(5, 5, 2, 2); g.fillRect(9, 9, 2, 2);
      g.fillRect(9, 5, 2, 1); g.fillRect(8, 6, 2, 1);
      g.fillRect(7, 7, 2, 1); g.fillRect(6, 8, 2, 1);
      g.fillRect(5, 9, 2, 1);
    });

    // Campo do ICMS: marcas ciano mostram o raio real; marcos dourados reforçam
    // a leitura de imposto. É uma textura estática que apenas gira no renderer.
    const rr = Renderer.ICMS_RING_R;
    this.icmsRingTex = mk(rr * 2 + 10, rr * 2 + 10, (g) => {
      const c = rr + 5;
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        g.save();
        g.translate(c, c);
        g.rotate(a);
        g.fillStyle = i % 6 === 0 ? 'rgba(255,214,63,0.9)' : 'rgba(48,211,232,0.62)';
        g.fillRect(-4, -rr - 1, 8, i % 6 === 0 ? 4 : 3);
        g.restore();
      }
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2 + Math.PI / 24;
        g.save();
        g.translate(c, c);
        g.rotate(a);
        g.fillStyle = i % 4 === 0 ? 'rgba(255,214,63,0.28)' : 'rgba(32,130,210,0.32)';
        g.fillRect(-2, -rr * 0.72, 4, 2);
        g.restore();
      }
    });
  }

  // Textura da sombra: elipse preta translúcida (alpha embutido) desenhada UMA
  // vez em canvas 2D. LINEAR pra suavizar quando escalada (sombra não é pixel
  // art). Exclusiva deste Renderer -> vai pra lista de destruição do destroy().
  private makeShadowTexture(): Texture {
    const base = Renderer.SHADOW_BASE;
    const w = base * 2, h = Math.ceil(base * 2 * 0.35);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const g = c.getContext('2d')!;
    g.fillStyle = 'rgba(0,0,0,0.3)';
    g.beginPath();
    g.ellipse(w / 2, h / 2, base, base * 0.35, 0, 0, Math.PI * 2);
    g.fill();
    const tex = new PIXI.Texture(new PIXI.BaseTexture(c, { scaleMode: PIXI.SCALE_MODES.LINEAR }));
    this.ownTextures.push(tex);
    return tex;
  }

  // Sombra elíptica sob a entidade: sprite da textura compartilhada, escalado
  // pro raio pedido (meia-largura w -> scale w/SHADOW_BASE, aspecto 0.35 já
  // embutido na textura). Todas batcham numa draw call só (issue #145).
  private makeShadow(w: number): Sprite {
    const sh = new PIXI.Sprite(this.shadowTex);
    sh.anchor.set(0.5);
    sh.scale.set(w / Renderer.SHADOW_BASE);
    return sh;
  }

  // Desliga o AccessibilityManager do Pixi (renderer.plugins.accessibility).
  // Remove keydown/tab hooks e o walk de updateAccessibleObjects no postrender.
  private disablePixiAccessibility(): void {
    const plugins = (this.app.renderer as { plugins?: Record<string, { destroy?: () => void }> }).plugins;
    const acc = plugins?.accessibility;
    if (acc && typeof acc.destroy === 'function') acc.destroy();
    if (plugins && 'accessibility' in plugins) delete plugins.accessibility;
  }

  // Corta o pipeline de eventos do renderer (move/global move) e marca stage
  // como não-interativo. UI do jogo é DOM (menu/urna/HUD), não pick de sprites.
  private disablePixiInteraction(): void {
    this.disableInteractive(this.app.stage);
    const events = (this.app.renderer as unknown as {
      events?: { features?: { move?: boolean; globalMove?: boolean; click?: boolean; wheel?: boolean } };
    }).events;
    if (events?.features) {
      events.features.move = false;
      events.features.globalMove = false;
      events.features.click = false;
      events.features.wheel = false;
    }
  }

  private disableInteractive(obj: Container): void {
    const o = obj as Container & {
      eventMode?: string;
      interactive?: boolean;
      interactiveChildren?: boolean;
    };
    if ('eventMode' in o) o.eventMode = 'none';
    o.interactive = false;
    o.interactiveChildren = false;
  }

  // Texturas 1×N da barrinha + anel de elite: desenhadas UMA vez; no hot path
  // só se mexe em width/position/visible dos sprites (batch de quads).
  private makeEnemyHudTextures(): void {
    const solid = (w: number, h: number, css: string): Texture => {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d')!;
      g.fillStyle = css;
      g.fillRect(0, 0, w, h);
      return this.ownTexture(c);
    };
    // 32×4: largura sobrescrita por sprite.width; alpha no bg embutido
    this.hpBarBgTex = solid(32, 4, 'rgba(0,0,0,0.6)');
    this.hpBarFillTex = solid(32, 4, '#d42a2a');

    // Anel dourado: círculo com stroke, centro em (32,32), raio ELITE_RING_TEX_RADIUS
    const size = ELITE_RING_TEX_RADIUS * 2 + 8;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const g = c.getContext('2d')!;
    const cx = size / 2, cy = size / 2;
    g.strokeStyle = 'rgba(255,209,23,0.9)';
    g.lineWidth = 3;
    g.beginPath();
    g.arc(cx, cy, ELITE_RING_TEX_RADIUS, 0, Math.PI * 2);
    g.stroke();
    this.eliteRingTex = this.ownTexture(c);
  }

  // ---------------------------------------------------------------------------
  // Ciclo de vida
  // ---------------------------------------------------------------------------
  startGame(startPayload: GameStartPayload, youId: string, getInput: () => { mx: number; my: number }): void {
    this.youId = youId;
    this.getInput = getInput;
    this.playersInfo = new Map(startPayload.players.map((p) => [p.id, p]));
    this.buffer = [];
    this.running = true;
    // Estado de dano flutuante de partida anterior não vaza pra esta
    this.enemyHp.clear();
    this.dmgAcc.clear();
    this.lastDmgSnap = null;
    for (const f of this.dmgFloats) { f.sp.visible = false; this.dmgFloatFree.push(f.sp); }
    this.dmgFloats.length = 0;
  }

  onSnapshot(snap: Snapshot): void {
    this.buffer.push({ at: performance.now(), snap });
    if (this.buffer.length > 30) this.buffer.shift();
    this.paused = !!snap.pause;
    const mine = snap.players.find((p) => p.id === this.youId);
    if (mine) {
      if (!this.myServer) { this.me.x = mine.x; this.me.y = mine.y; }
      this.myServer = mine;
    }
  }

  // ---------------------------------------------------------------------------
  // Frame: predição própria + interpolação do resto
  // ---------------------------------------------------------------------------
  private frame(dt: number): void {
    if (!this.running || this.buffer.length === 0) return;

    // Orçamentos de FX deste frame (wipe monstro não vira 800 bursts + sfx)
    this.burstBudget = BURSTS_PER_FRAME;
    this.killSfxBudget = KILL_SFX_PER_FRAME;

    this.updateDynamicResolution(dt);

    // ---- Efeitos ambientes (independem da simulação; seguem na pausa) ------
    this.bgTime += dt;
    this.waterFxG.alpha = 0.55 + 0.3 * Math.sin(this.bgTime * 1.2);
    this.waterFxG.x = Math.sin(this.bgTime * 0.6) * 8;

    // ---- PREDIÇÃO do próprio jogador (congela junto com a pausa da urna) ----
    const my = this.myServer;
    if (my && my.st === PSTATE.ALIVE && !my.frz && !this.paused) {
      const { mx, my: myY } = this.getInput();
      this.me.x = Math.min(Math.max(this.me.x + mx * my.spd * dt, PLAYER_RADIUS), ARENA_W - PLAYER_RADIUS);
      this.me.y = Math.min(Math.max(this.me.y + myY * my.spd * dt, PLAYER_RADIUS), ARENA_H - PLAYER_RADIUS);
    }
    // Correção suave rumo à posição autoritativa
    if (my) {
      const ex = my.x - this.me.x, ey = my.y - this.me.y;
      const err = Math.hypot(ex, ey);
      if (err > 220 || (my.st !== PSTATE.ALIVE)) { this.me.x = my.x; this.me.y = my.y; }
      else { const k = Math.min(1, dt * 4); this.me.x += ex * k; this.me.y += ey * k; }
    }

    // ---- INTERPOLAÇÃO: renderiza o passado (INTERP_DELAY_MS atrás) ----------
    const rt = performance.now() - INTERP_DELAY_MS;
    const buf = this.buffer;
    const last = buf[buf.length - 1];
    let a: { at: number; snap: Snapshot }, b: { at: number; snap: Snapshot }, alpha: number;

    if (rt >= last.at) {
      // Buffer seco (snapshot atrasado/perdido): em vez de congelar e "pular"
      // quando o pacote chegar, EXTRAPOLA pela velocidade dos 2 últimos
      // snapshots (dead reckoning), por um tempo limitado. Passado o teto, a
      // posição para (não sai voando num stall real) até o próximo snapshot.
      a = buf[buf.length - 2] ?? last;
      b = last;
      const span = Math.max(b.at - a.at, 1);
      const ahead = Math.min(rt - b.at, EXTRAP_MAX_MS);
      alpha = (span + ahead) / span;                 // >= 1 => além do último snapshot
    } else {
      // Interpolação normal: acha o par de snapshots que cerca o tempo de render
      a = buf[0]; b = last;
      for (let i = 0; i < buf.length - 1; i++) {
        if (buf[i].at <= rt && buf[i + 1].at >= rt) { a = buf[i]; b = buf[i + 1]; break; }
      }
      const span = Math.max(b.at - a.at, 1);
      alpha = Math.min(Math.max((rt - a.at) / span, 0), 1);
    }
    const simTime = a.snap.t + (b.snap.t - a.snap.t) * alpha;

    // Limites de culling em mundo: meia-tela / zoom + margem, ao redor da câmera
    const z = this.viewZoom();
    const halfW = this.app.screen.width / 2 / z + Renderer.CULL_MARGIN;
    const halfH = this.app.screen.height / 2 / z + Renderer.CULL_MARGIN;
    this.cullMinX = this.me.x - halfW; this.cullMaxX = this.me.x + halfW;
    this.cullMinY = this.me.y - halfH; this.cullMaxY = this.me.y + halfH;

    this.drawPlayers(a.snap, b.snap, alpha, dt);
    // Antes do drawKeyed: inimigo morto ainda está no pool (posição do número)
    this.trackDamage(b.snap);
    this.drawKeyed('enemies', a.snap.enemies, b.snap.enemies, alpha,
      (arr: EnemySnap) => this.enemySprite(arr), (sp, arr) => this.enemyUpdate(sp, arr, dt));
    this.drawKeyed('allies', a.snap.allies, b.snap.allies, alpha, (arr: AllySnap) => this.allySprite(arr));
    this.drawKeyed('projs', a.snap.projs, b.snap.projs, alpha,
      (arr: ProjSnap) => this.projSprite(arr), (sp, arr, dx, dy) => this.projUpdate(sp, arr, dx, dy, dt));
    this.drawKeyed('gems', a.snap.gems, b.snap.gems, alpha,
      (arr: GemSnap) => this.gemSprite(arr), (sp, arr) => { sp.pivot.y = Math.sin(performance.now() / 250 + arr[0]) * 2; });
    this.drawKeyed('coins', a.snap.coins ?? [], b.snap.coins ?? [], alpha,
      (arr: CoinSnap) => this.coinSprite(arr), (sp, arr) => { sp.pivot.y = Math.sin(performance.now() / 220 + arr[0]) * 2; });
    this.drawKeyed('chests', a.snap.chests ?? [], b.snap.chests ?? [], alpha,
      (arr: ChestSnap) => this.chestSprite(arr), (sp, arr) => { sp.pivot.y = Math.sin(performance.now() / 350 + arr[0]) * 3; });
    this.drawZones(b.snap.zones);
    this.drawAuras(b.snap, simTime);
    this.updateTransients(dt);
    this.updateParticles(dt);
    this.updateDmgFloats(dt);

    // ---- CÂMERA segue a posição predita (+ screen shake) ---------------------
    this.shake = Math.max(0, this.shake - dt * 30);
    const sx = this.shake ? (Math.random() - 0.5) * this.shake : 0;
    const sy = this.shake ? (Math.random() - 0.5) * this.shake : 0;
    this.world.scale.set(this.viewZoom());
    this.world.pivot.set(this.me.x, this.me.y);
    this.world.position.set(this.app.screen.width / 2 + sx, this.app.screen.height / 2 + sy);

    // Vinheta cobre a tela toda
    this.vignette.width = this.app.screen.width;
    this.vignette.height = this.app.screen.height;
  }

  // Zoom da câmera por tamanho de viewport (issue #104): em tela de celular a
  // janela 1:1 do mundo era minúscula — na dificuldade alta o inimigo rápido
  // entrava na tela já conectando o hit, sem tempo de reação (fairness, não
  // conforto). Escala proporcional à diagonal da tela com referência num
  // desktop comum e piso de 0.7 (senão vira mapa de guerra ilegível).
  // AOI_RADIUS (1700) segue cobrindo com folga a meia-diagonal visível: no
  // pior caso do piso (celular ~930px de diagonal / 0.7) são ~660 un. de mundo.
  private static readonly ZOOM_REF_DIAG = 1550;  // diagonal que ganha escala 1
  private static readonly ZOOM_MIN = 0.7;

  private viewZoom(): number {
    const diag = Math.hypot(this.app.screen.width, this.app.screen.height);
    return Math.max(Renderer.ZOOM_MIN, Math.min(1, diag / Renderer.ZOOM_REF_DIAG));
  }

  // Interpola listas [id, ..., x, y, ...] casando por id entre snapshot A e B.
  // Genérico sobre a tupla T e a view V; cada pool é homogêneo por `kind`,
  // então os casts localizados (id/coordenadas numéricas e `pool.get` -> V)
  // são seguros dentro deste hot loop.
  private drawKeyed<T extends readonly unknown[], V extends EntityView>(
    kind: PoolKind, listA: readonly T[], listB: readonly T[], alpha: number,
    make: (arr: T) => V, extra?: (sp: V, arr: T, dx: number, dy: number) => void,
  ): void {
    const xi = kind === 'gems' || kind === 'coins' || kind === 'chests' ? 1 : 2;
    const mapA = this.scratchMapA;
    mapA.clear();
    for (const ea of listA) mapA.set(ea[0] as number, ea);
    const pool = this.sprites[kind];
    const seen = this.scratchSeen;
    seen.clear();
    for (const eb of listB) {
      const id = eb[0] as number;
      seen.add(id);
      let sp = pool.get(id) as V | undefined; // só `make`/acquire inserem neste pool
      if (!sp) { sp = this.acquireView(kind, eb, make); pool.set(id, sp); }
      const ea = mapA.get(id) as T | undefined;
      // Coordenadas nas posições [xi] e [xi+1] das tuplas são sempre numéricas
      const bx = eb[xi] as number, by = eb[xi + 1] as number;
      const x = ea ? (ea[xi] as number) + (bx - (ea[xi] as number)) * alpha : bx;
      const y = ea ? (ea[xi + 1] as number) + (by - (ea[xi + 1] as number)) * alpha : by;
      // Culling (issue #145): fora da tela vira invisível e pula sombra + update.
      const onScreen = x >= this.cullMinX && x <= this.cullMaxX
        && y >= this.cullMinY && y <= this.cullMaxY;
      // Off-screen: só atualiza posição (pra reentrar certo) e esconde — sem
      // shadow write, sem extra (enemyUpdate/pivot), sem alocar trabalho de tint.
      if (!onScreen) {
        if (sp.visible) sp.visible = false;
        if (sp._shadow?.visible) sp._shadow.visible = false;
        sp.position.set(x, y);
        continue;
      }
      const dx = ea ? bx - (ea[xi] as number) : 0, dy = ea ? by - (ea[xi + 1] as number) : 0;
      sp.position.set(x, y);
      if (!sp.visible) sp.visible = true;
      if (sp._shadow) {
        sp._shadow.visible = true;
        sp._shadow.position.set(x, y + (sp._shadowDy ?? 0));
      }
      if (extra) extra(sp, eb, dx, dy);
    }
    for (const [id, sp] of pool) {
      if (!seen.has(id)) {
        // Inimigo sumiu: morreu OU saiu do AOI. FX só se ESTAVA na tela
        // (culling da câmera) — kill off-screen no wipe não aloca partículas.
        if (kind === 'enemies' && this.running
            && deathFxOnScreen(sp.x, sp.y, this.cullMinX, this.cullMaxX, this.cullMinY, this.cullMaxY)) {
          this.spawnDeathBurst(sp.x, sp.y, sp._cor ?? 0xffffff, !!sp._isBoss);
          this.playKillSfx();
        }
        this.releaseView(sp);
        pool.delete(id);
      }
    }
  }

  private playKillSfx(): void {
    if (this.killSfxBudget <= 0) return;
    this.killSfxBudget--;
    audio.sfx.kill();
  }

  // Reusa uma view do free-list (ou cria via `make`). Views recicladas ficam
  // penduradas na camada com visible=false, então a ordem de desenho persiste.
  private acquireView<T extends readonly unknown[], V extends EntityView>(
    kind: PoolKind, arr: T, make: (arr: T) => V,
  ): V {
    const key = Renderer.viewKey(kind, arr);
    const sp = this.freeViews.get(key)?.pop() as V | undefined;
    if (!sp) {
      const nv = make(arr);
      nv._poolKey = key;
      return nv;
    }
    // Estado por-entidade de volta ao de fábrica (a view veio de outro dono)
    sp.visible = true;
    if (sp._shadow) sp._shadow.visible = true;
    sp.pivot.y = 0;                          // gems/chests balançam o pivot
    if (kind === 'enemies') {
      const e = sp as EntityView as EnemyView;
      e.alpha = 1;
      e._pop = 0;
      e._hpF = 1;
      e._hpBarF = -1;
      e._hpBarY = 0;
      e._hpBarW = 0;
      e._frozen = undefined;
      e._elite = undefined;
      e._walkFrame = -1;
      e._body.tint = 0xffffff;
      e._body.scale.set(e._baseScale);
      if (e._hpBarBg) e._hpBarBg.visible = false;
      if (e._hpBarFill) e._hpBarFill.visible = false;
      if (e._eliteRing) e._eliteRing.visible = false; // enemyUpdate reexibe se elite
    } else if (kind === 'projs') {
      sp.rotation = 0;                       // gavel/pan/cachaca giram
      sp.scale.y = Math.abs(sp.scale.y);     // projéteis mirados flipam o Y
    }
    return sp;
  }

  private releaseView(sp: EntityView): void {
    sp.visible = false;
    if (sp._shadow) sp._shadow.visible = false;
    const key = sp._poolKey ?? '';
    let list = this.freeViews.get(key);
    if (!list) this.freeViews.set(key, (list = []));
    if (list.length < Renderer.FREE_CAP) list.push(sp);
    else { sp._shadow?.destroy(); sp.destroy({ children: true }); }
  }

  // Subtipo do free-list: views são heterogêneas dentro do mesmo kind
  private static viewKey(kind: PoolKind, arr: readonly unknown[]): string {
    if (kind === 'chests' || kind === 'coins') return kind;
    if (kind === 'gems') return `gems:${(arr[3] as number) >= 4 ? 'big' : 'small'}`;
    return `${kind}:${arr[1]}`; // enemies: índice do tipo; projs/allies: kind
  }

  // ---------------------------------------------------------------------------
  // Sprites de cada tipo de entidade
  // ---------------------------------------------------------------------------
  private attachShadow(sp: EntityView, w: number, dy: number): void {
    sp._shadow = this.makeShadow(w);
    sp._shadowDy = dy;
    this.layers.shadows.addChild(sp._shadow);
  }

  private enemySprite(arr: EnemySnap): EnemyView {
    const type = ETYPE_IDS[arr[1]];
    const def = typeDef(type)!; // o servidor só envia índices de tipo válidos
    const isBoss = 'ai' in def; // só BossDef tem `ai`
    const c = new PIXI.Container() as EnemyView;

    const em = new PIXI.Sprite(spriteFactory.tex(ENEMY_SPRITE[type] ?? 'urna_possuida'));
    em.anchor.set(0.5, 0.6);
    const scale = (def.radius * 2.4) / em.texture.height;
    em.scale.set(scale);
    c.addChild(em);
    c._body = em;
    c._baseScale = scale;
    c._cor = def.cor;
    c._isBoss = isBoss;
    c._hpF = 1;
    c._pop = 0;
    c._hpBarF = -1;
    c._hpBarY = 0;
    c._hpBarW = 0;
    c._walkFrame = -1;
    c._drawn = ENEMY_DRAWN[type];
    c._radius = def.radius;

    if (isBoss) {
      const nome = new PIXI.Text(def.nome, {
        fontSize: 16, fill: 0xffd117, fontWeight: 'bold', stroke: 0x000000, strokeThickness: 4,
      });
      nome.anchor.set(0.5);
      nome.y = -def.radius * 1.5 - 14;
      c.addChild(nome);
      c._nome = nome;
    }
    this.attachShadow(c, def.radius * 0.9, def.radius * 0.85);
    this.layers.enemies.addChild(c);
    return c;
  }

  private enemyUpdate(sp: EnemyView, arr: EnemySnap, dt: number): void {
    const flags = arr[5], hpF = arr[4];
    const frozen = isFrozenFlag(flags);
    const elite = isEliteFlag(flags);
    const ghost = isGhostFlag(flags);

    // Alpha / tint só quando o estado muda (profile: set tint em massa)
    const wantAlpha = ghost ? 0.5 : 1;
    if (sp.alpha !== wantAlpha) sp.alpha = wantAlpha;
    if (sp._frozen !== frozen) {
      sp._frozen = frozen;
      sp._body.tint = frozen ? 0x77c9ff : 0xffffff;
      // Congelar/descongelar invalida o índice de frame pra re-aplicar texture
      sp._walkFrame = -1;
    }

    // Sprite desenhado: só troca texture quando o frame (ou freeze) muda
    const frames = sp._drawn ? fxFrames(sp._drawn) : null;
    if (frames) {
      const frameIdx = frozen ? 0 : Math.floor(performance.now() / 150) % frames.length;
      if (sp._walkFrame !== frameIdx) {
        sp._walkFrame = frameIdx;
        const tex = frames[frameIdx];
        if (sp._body.texture !== tex) {
          sp._body.texture = tex;
          // Arte desenhada aguenta aparecer maior que o hitbox (como os players)
          sp._baseScale = (sp._radius * 3.1) / tex.height;
          sp._body.anchor.set(0.5, 0.7); // pés na sombra
          // Retrato é mais alto que o procedural: sobe a placa de nome do boss
          if (sp._nome) sp._nome.y = -sp._baseScale * tex.height * 0.7 - 16;
        }
      }
    }

    // Anel de elite: sprite de textura compartilhada (sem Graphics no hot path)
    if (elite && !sp._eliteRing) {
      const ring = new PIXI.Sprite(this.eliteRingTex);
      ring.anchor.set(0.5);
      ring.tint = ELITE_RING_COLOR;
      sp.addChildAt(ring, 0);
      sp._eliteRing = ring;
    }
    if (sp._elite !== elite) {
      sp._elite = elite;
      if (sp._eliteRing) sp._eliteRing.visible = elite;
    }
    if (elite && sp._eliteRing) {
      // Escala acompanha o body (pop de dano / atlas swap)
      const s = eliteRingScale(sp._body.height, ELITE_RING_TEX_RADIUS);
      if (sp._eliteRing.scale.x !== s) sp._eliteRing.scale.set(s);
    }

    // Tomou dano? "Pop" de escala (juice barata e eficaz)
    if (hpF < sp._hpF - 0.001) sp._pop = 1;
    sp._hpF = hpF;
    sp._pop = Math.max(0, sp._pop - dt * 6);
    sp._body.scale.set(sp._baseScale * (1 + sp._pop * 0.18));

    // Barrinha de HP: sprites bg+fill; só mexe em width/y quando muda
    const layout = layoutEnemyHpBar({
      hpFrac: hpF,
      bodyHeight: sp._body.height,
      isBoss: sp._isBoss,
      elite,
    });
    if (!layout.visible) {
      if (sp._hpBarBg) sp._hpBarBg.visible = false;
      if (sp._hpBarFill) sp._hpBarFill.visible = false;
      return;
    }
    if (!sp._hpBarBg) {
      const bg = new PIXI.Sprite(this.hpBarBgTex);
      bg.anchor.set(0, 0.5);
      sp.addChild(bg);
      sp._hpBarBg = bg;
      const fill = new PIXI.Sprite(this.hpBarFillTex);
      fill.anchor.set(0, 0.5);
      fill.tint = ENEMY_HP_FILL_COLOR;
      sp.addChild(fill);
      sp._hpBarFill = fill;
    }
    if (sp._hpBarF !== hpF || sp._hpBarY !== layout.y || sp._hpBarW !== layout.barW) {
      sp._hpBarF = hpF;
      sp._hpBarY = layout.y;
      sp._hpBarW = layout.barW;
      const bg = sp._hpBarBg!;
      const fill = sp._hpBarFill!;
      bg.visible = true;
      fill.visible = layout.fillW > 0;
      bg.position.set(layout.leftX, layout.y);
      bg.width = layout.barW;
      bg.height = layout.barH;
      fill.position.set(layout.leftX, layout.y);
      fill.width = layout.fillW;
      fill.height = layout.barH;
      // bg color is baked into texture alpha; keep tint neutral
      bg.tint = ENEMY_HP_BG_COLOR === 0x000000 ? 0xffffff : ENEMY_HP_BG_COLOR;
    }
  }

  private allySprite(arr: AllySnap): EntityView {
    const kind = arr[1];
    const c = new PIXI.Container() as EntityView;
    const ring = new PIXI.Graphics();
    ring.lineStyle(2, 0x25d366, 0.9).drawCircle(0, 0, 20); // anel verde-PIX: comprado
    c.addChild(ring);
    const em = new PIXI.Sprite(spriteFactory.tex(ENEMY_SPRITE[kind] ?? 'corrupto'));
    em.anchor.set(0.5, 0.6);
    em.scale.set(2.2);
    c.addChild(em);
    this.attachShadow(c, 16, 16);
    this.layers.allies.addChild(c);
    return c;
  }

  private projSprite(arr: ProjSnap): ProjView {
    const kind = arr[1];
    // Sprite desenhado (assets/fx) quando existe e já carregou; senão procedural
    const drawn = fxTex(kind);
    const em = new PIXI.Sprite(drawn ?? spriteFactory.tex(PROJ_SPRITE[kind] ?? 'proj_fake')) as ProjView;
    em.anchor.set(0.5);
    // Sprites de spells.png vêm em ~44-64px: 0.9 os deixa em ~40px (na escala do
    // jogador, 44px); o procedural continua em 2x e a moto em 3x. Alguns têm
    // escala própria (PROJ_FX_SCALE) — a mãozinha do "L" é maior.
    em.scale.set(kind === 'moto' ? 3 : drawn ? (PROJ_FX_SCALE[kind] ?? 0.9) : 2);
    em._kind = kind;
    this.layers.projs.addChild(em);
    return em;
  }

  private projUpdate(sp: ProjView, _arr: ProjSnap, dx: number, dy: number, dt: number): void {
    if (SPIN_PROJ.has(sp._kind)) sp.rotation += dt * 12;
    else if (AIM_PROJ.has(sp._kind) && (dx || dy)) {
      sp.rotation = Math.atan2(dy, dx);
      sp.scale.y = Math.abs(sp.scale.y) * (dx < 0 ? -1 : 1); // não voa de cabeça pra baixo
    }
  }

  private gemSprite(arr: GemSnap): Sprite {
    const big = arr[3] >= 4;
    const g = new PIXI.Sprite(spriteFactory.tex(big ? 'gem_voto_big' : 'gem_voto'));
    g.anchor.set(0.5);
    g.scale.set(big ? 2 : 1.6);
    this.layers.gems.addChild(g);
    return g;
  }

  // Maço de Caixa 2 (#90/#94): dinheiro vivo esperando dono na mesma camada dos votos
  private coinSprite(_arr: CoinSnap): Sprite {
    const c = new PIXI.Sprite(spriteFactory.tex('maco_caixa2'));
    c.anchor.set(0.5);
    c.scale.set(1.7);
    this.layers.gems.addChild(c);
    return c;
  }

  private chestSprite(_arr: ChestSnap): EntityView {
    const c = new PIXI.Container() as EntityView;
    const glow = new PIXI.Graphics();
    glow.beginFill(0xf7d117, 0.12).drawCircle(0, 0, 34).endFill();
    glow.lineStyle(2, 0xf7d117, 0.35).drawCircle(0, 0, 30);
    c.addChild(glow);
    const em = new PIXI.Sprite(spriteFactory.tex('mala'));
    em.anchor.set(0.5, 0.6);
    em.scale.set(2.6);
    c.addChild(em);
    this.attachShadow(c, 18, 14);
    this.layers.chests.addChild(c);
    return c;
  }

  // ---------------------------------------------------------------------------
  // Jogadores (com estado de caído/revive)
  // ---------------------------------------------------------------------------
  private drawPlayers(snapA: Snapshot, snapB: Snapshot, alpha: number, dt: number): void {
    const mapA = this.playersMapA;
    mapA.clear();
    for (const pa of snapA.players) mapA.set(pa.id, pa);
    const pool = this.sprites.players;
    const seen = this.playersSeen;
    seen.clear();

    for (const pb of snapB.players) {
      seen.add(pb.id);
      let sp = pool.get(pb.id);
      if (!sp) { sp = this.playerSprite(pb); pool.set(pb.id, sp); }

      sp.visible = pb.st === PSTATE.ALIVE || pb.st === PSTATE.DOWNED;
      if (sp._shadow) sp._shadow.visible = sp.visible;

      if (pb.id === this.youId) {
        sp.position.set(this.me.x, this.me.y);
      } else {
        const pa = mapA.get(pb.id);
        sp.position.set(
          pa ? pa.x + (pb.x - pa.x) * alpha : pb.x,
          pa ? pa.y + (pb.y - pa.y) * alpha : pb.y);
      }
      sp._shadow?.position.set(sp.x, sp.y + PLAYER_RADIUS * 0.85);

      // Vetor de movimento: input local pro próprio jogador, delta de snapshot
      // pros demais (escalas diferentes, mas só o sinal/razão importam aqui)
      const pa = mapA.get(pb.id);
      const vx = pb.id === this.youId ? this.getInput().mx : (pa ? pb.x - pa.x : 0);
      const vy = pb.id === this.youId ? this.getInput().my : (pa ? pb.y - pa.y : 0);

      if (this.animateSheet(sp, vx, vy, dt)) {
        // sprite sheet cuida de direção e frames
      } else if (vx > 0.1) sp._body.scale.x = Math.abs(sp._body.scale.x);
      else if (vx < -0.1) sp._body.scale.x = -Math.abs(sp._body.scale.x);

      // Barrinha de HP (hp vem quantizado do snapshot: só redesenha se mudou)
      const hpFrac = pb.maxHp ? Math.max(pb.hp / pb.maxHp, 0) : 0;
      if (sp._hpFrac !== hpFrac) {
        sp._hpFrac = hpFrac;
        sp._hp.clear();
        sp._hp.beginFill(0x000000, 0.6).drawRect(-20, -PLAYER_RADIUS - 16, 40, 5).endFill();
        sp._hp.beginFill(hpFrac > 0.35 ? 0x2ecc71 : 0xd42a2a).drawRect(-20, -PLAYER_RADIUS - 16, 40 * hpFrac, 5).endFill();
      }

      // Anel de status: redesenhar arcos/círculos todo frame re-triangula à toa
      const statusKey = `${pb.st}|${pb.rev}|${pb.inv}|${pb.frz}`;
      if (sp._statusKey !== statusKey) {
        sp._statusKey = statusKey;
        sp._status.clear();
        if (pb.st === PSTATE.DOWNED) {
          sp._status.lineStyle(4, 0xffd117, 0.9).arc(0, 0, PLAYER_RADIUS + 8, -Math.PI / 2, -Math.PI / 2 + pb.rev * Math.PI * 2);
          sp._status.lineStyle(2, 0xd42a2a, 0.8).drawCircle(0, 0, PLAYER_RADIUS + 8);
        } else {
          if (pb.inv) sp._status.lineStyle(3, 0xffffff, 0.7).drawCircle(0, 0, PLAYER_RADIUS + 5);
          if (pb.frz) sp._status.lineStyle(3, 0x66bbff, 0.9).drawCircle(0, 0, PLAYER_RADIUS + 3);
        }
      }
      let label: string;
      if (pb.st === PSTATE.DOWNED) {
        sp.alpha = 0.65;
        sp._body.tint = 0x8899aa;
        label = `☠ ${pb.bleed}s`;
      } else {
        sp.alpha = 1;
        sp._body.tint = pb.frz ? 0x99ccff : 0xffffff;
        label = this.playersInfo.get(pb.id)?.name ?? '';
      }
      if (!pb.con) label += ' 📴';
      // Atribuir Text suja o canvas inteiro: só quando o texto muda de fato
      if (sp._label.text !== label) sp._label.text = label;
    }

    for (const [id, sp] of pool) {
      if (!seen.has(id)) { sp._shadow?.destroy(); sp.destroy({ children: true }); pool.delete(id); }
    }
  }

  // Personagem com atlas (charSheets): escolhe direção pelo vetor de movimento,
  // avança o ciclo de caminhada e espelha o perfil quando anda pra esquerda.
  // Retorna false enquanto o atlas não carregou (fica no sprite procedural).
  private animateSheet(sp: PlayerView, vx: number, vy: number, dt: number): boolean {
    const sheet = charSheet(sp._charId, sp._skinId);
    if (!sheet) return false;
    if (!sp._sheetOn) {
      // Troca do procedural pro atlas: célula 64px, personagem ocupa ~60px
      // (altura normalizada pelo build_char_sheets.py) com pés na base
      sp._sheetOn = true;
      sp._body.anchor.set(0.5, 0.66);
      sp._body.scale.set((PLAYER_RADIUS * 2.7) / 64);
    }
    const moving = Math.abs(vx) > 0.1 || Math.abs(vy) > 0.1;
    if (moving) {
      // Histerese leve pro lado: só vira perfil se X domina o movimento
      sp._sheetDir = Math.abs(vx) > Math.abs(vy) ? 'side' : vy < 0 ? 'up' : 'down';
      if (vx > 0.1) sp._facing = 1;
      else if (vx < -0.1) sp._facing = -1;
      sp._animT += dt;
    } else {
      sp._animT = 0;
    }
    const frames = sheet.walk[sp._sheetDir];
    // Cadência: ciclo completo em ~0.6s independente do nº de frames
    const tex = moving
      ? frames[Math.floor(sp._animT * (frames.length / 0.6)) % frames.length]
      : sheet.idle[sp._sheetDir];
    if (sp._body.texture !== tex) sp._body.texture = tex;
    // O perfil do atlas olha pra ESQUERDA: espelha quando anda pra direita
    const flip = sp._sheetDir === 'side' && sp._facing > 0 ? -1 : 1;
    sp._body.scale.x = Math.abs(sp._body.scale.y) * flip;
    return true;
  }

  private playerSprite(pb: PlayerSnap): PlayerView {
    const info = this.playersInfo.get(pb.id);
    const char = CHARACTER_TABLE[info?.charId ?? ''] ?? Object.values(CHARACTERS)[0];
    const c = new PIXI.Container() as PlayerView;
    c._charId = char.id;
    c._skinId = info?.skinId ?? DEFAULT_SKIN;
    c._sheetOn = false;
    c._sheetDir = 'down';
    c._facing = 1;
    c._animT = 0;
    c._hpFrac = -1;
    c._statusKey = '';

    // Anel da cor do partido (leitura rápida de quem é quem)
    const ring = new PIXI.Graphics();
    ring.lineStyle(2, char.cor, 0.55).drawCircle(0, 4, PLAYER_RADIUS + 2);
    c.addChild(ring);

    const em = new PIXI.Sprite(spriteFactory.tex(skinSpriteId(char.id, c._skinId)));
    em.anchor.set(0.5, 0.58);
    em.scale.set((PLAYER_RADIUS * 2.6) / em.texture.height);
    c.addChild(em);
    c._body = em;

    const label = new PIXI.Text(info?.name ?? '', {
      fontSize: 13, fill: pb.id === this.youId ? 0xffd117 : 0xffffff,
      stroke: 0x000000, strokeThickness: 3, fontWeight: 'bold',
    });
    label.anchor.set(0.5);
    label.y = PLAYER_RADIUS + 16;
    c.addChild(label);
    c._hp = new PIXI.Graphics(); c.addChild(c._hp);
    c._status = new PIXI.Graphics(); c.addChild(c._status);
    c._label = label;

    this.attachShadow(c, PLAYER_RADIUS * 0.9, PLAYER_RADIUS * 0.85);
    this.layers.players.addChild(c);
    return c;
  }

  // ---------------------------------------------------------------------------
  // Zonas de perigo (telegrafadas) e zonas amigas (Lava Jato, cachaça)
  // ---------------------------------------------------------------------------
  private readonly zoneBooms = new Map<number, PuddleView>(); // explosão animada por zona
  private readonly zoneFx = new Map<number, ZoneFxView>();    // raízes/tornado desenhados por zona

  // Idade (s) em que o estouro troca de frame: garrafa -> BOOM -> assentando;
  // depois fica a fumaça queimando até a zona secar
  private static readonly BOOM_KEYS = [0.08, 0.34, 0.62];

  private drawZones(zones: ZoneSnap[]): void {
    const boomFrames = fxFrames('cachaca_explosion');
    // Geometria depende só do snapshot: mesma ref => mesmo desenho, pula tudo
    if (zones !== this.lastZonesRef) {
      this.lastZonesRef = zones;
      const g = this.zonesG;
      g.clear();
      const seenBooms = new Set<number>();
      const seenFx = new Set<number>();
      for (const [id, kind, x, y, r, frac] of zones) {
        const cor = ZONE_COLORS[kind] ?? 0xd42a2a;
        const friendly = FRIENDLY_ZONES.has(kind);
        // Zona com sprite desenhado (raízes da Marina, tornado errante): o
        // desenho (animado por wall-clock abaixo) troca o círculo chapado
        if (ZONE_FX[kind] && fxTex(ZONE_FX[kind])) {
          seenFx.add(id);
          if (frac < 1) g.lineStyle(2, cor, 0.3).drawCircle(x, y, r); // telegrafa
          continue;
        }
        if (friendly && frac < 1) {
          // Garrafa ainda no ar: só a mira discreta de onde o estouro vai nascer
          g.lineStyle(2, cor, 0.35).drawCircle(x, y, r);
          continue;
        }
        // Cachaça pousou: o sprite do estouro (animado abaixo, por wall-clock)
        // substitui o círculo chapado; aqui só o raio real do dps, discreto
        if (kind === 'cachaca' && boomFrames) {
          seenBooms.add(id);
          g.lineStyle(2, cor, 0.25).drawCircle(x, y, r);
          continue;
        }
        g.lineStyle(3, cor, friendly ? 0.7 : 0.9).drawCircle(x, y, r);
        g.beginFill(cor, friendly ? 0.10 : 0.12).drawCircle(x, y, r).endFill();
        if (!friendly) g.beginFill(cor, 0.30).drawCircle(x, y, r * frac).endFill();
      }
      // Zona sumiu do snapshot (queimou tudo): remove o sprite
      for (const [id, sp] of this.zoneBooms) {
        if (!seenBooms.has(id)) {
          sp.destroy();
          this.zoneBooms.delete(id);
        }
      }
      for (const [id, sp] of this.zoneFx) {
        if (!seenFx.has(id)) {
          sp.destroy();
          this.zoneFx.delete(id);
        }
      }
    }
    // Raízes/tornado desenhados animam por wall-clock (surgir do chão, girar):
    // roda todo frame mesmo sem redraw do snapshot
    for (const [id, kind, x, y, r, frac] of zones) {
      const fxName = ZONE_FX[kind];
      const tex = fxName ? fxTex(fxName) : null;
      if (!tex) continue;
      let sp = this.zoneFx.get(id);
      if (!sp) {
        sp = new PIXI.Sprite(tex) as ZoneFxView;
        sp._born = performance.now();
        sp._kind = kind;
        // raiz apoia a base perto do centro da zona e sobe; tornado é centrado
        sp.anchor.set(0.5, kind === 'tornado' ? 0.5 : 0.72);
        this.layers.zones.addChild(sp);
        this.zoneFx.set(id, sp);
      }
      const age = (performance.now() - sp._born) / 1000;
      // raízes menores que o raio (pedido); tornado cobre a zona toda
      const base = (r * (kind === 'tornado' ? 2.1 : 1.45)) / tex.width;
      sp.position.set(x, y);
      if (kind === 'tornado') {
        sp.rotation = age * 6;                       // gira sem parar
        sp.scale.set(base * Math.min(frac + 0.3, 1));
      } else {
        // raízes crescem do chão: escala Y sobe de 0.2 -> 1 em ~0.25s
        const grow = Math.min(age / 0.25, 1);
        sp.scale.set(base, base * (0.2 + 0.8 * grow));
      }
      // tornado é grande: deixa translúcido pra não tampar a horda atrás
      sp.alpha = Math.min(age / 0.15, 1) * (kind === 'tornado' ? 0.7 : 1);
    }
    // Estouro da cachaça anima por wall-clock: roda todo frame, mesmo sem redraw
    if (!boomFrames) return;
    for (const [id, kind, x, y, r, frac] of zones) {
      if (kind !== 'cachaca' || frac < 1) continue;
      let sp = this.zoneBooms.get(id);
      if (!sp) {
        sp = new PIXI.Sprite(boomFrames[0]) as PuddleView;
        sp._born = performance.now();
        // Frames alinhados pela BASE no strip: âncora perto do fundo pra
        // explosão crescer a partir do centro da zona
        sp.anchor.set(0.5, 0.9);
        this.layers.zones.addChild(sp);
        this.zoneBooms.set(id, sp);
      }
      const age = (performance.now() - sp._born) / 1000;
      const idx = Renderer.BOOM_KEYS.findIndex((k) => age < k);
      const tex = boomFrames[idx === -1 ? boomFrames.length - 1 : idx];
      if (sp.texture !== tex) sp.texture = tex;
      sp.position.set(x, y);
      sp.scale.set((r * 2.3) / tex.width);
    }
  }

  // Auras contínuas: #FiqueEmCasa (doses orbitando), Cruz Credo (rosário orbital)
  // e Rachadinha — sprites de um pool, reposicionados por frame
  private aura(tex: Texture): Sprite {
    let sp = this.auraPool[this.auraUsed];
    if (!sp) {
      sp = new PIXI.Sprite(tex);
      this.layers.fx.addChild(sp);
      this.auraPool.push(sp);
    }
    if (sp.texture !== tex) sp.texture = tex;
    sp.visible = true;
    sp.rotation = 0;
    sp.alpha = 1;   // pool compartilhado: reseta ou a opacidade da aura vaza
    this.auraUsed++;
    return sp;
  }

  // Uma cruz + dois pontos de rastro dentro da janela angular de dano (0.28rad
  // no servidor). `trailStep` leva o sinal do giro para funcionar nos dois
  // sentidos da Procissão Giratória.
  private drawCruzOrbiter(
    tex: Texture, x: number, y: number, angle: number, radius: number,
    size: number, trailStep: number, phase: number,
  ): void {
    for (let t = 2; t >= 1; t--) {
      const a = angle - trailStep * t;
      const trail = this.aura(this.cruzTrailTex);
      trail.anchor.set(0.5);
      trail.scale.set(t === 1 ? 0.9 : 0.68);
      trail.alpha = t === 1 ? 0.68 : 0.38;
      trail.position.set(x + Math.cos(a) * radius, y + Math.sin(a) * radius);
    }

    const cruz = this.aura(tex);
    cruz.anchor.set(0.5);
    cruz.scale.set((size / tex.width) * (1 + Math.sin(phase * 5) * 0.035));
    cruz.rotation = Math.sin(phase * 3.2) * 0.045;
    cruz.position.set(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
  }

  private drawAuras(snap: Snapshot, simTime: number): void {
    this.auraUsed = 0;
    // Crucifixo dedicado quando carregado; senão a textura vetorial de fallback
    const cruzTex = fxTex('cruz_credo') ?? this.auraCruzTex;
    for (const pb of snap.players) {
      if (pb.st !== PSTATE.ALIVE) continue;
      const pos: { x: number; y: number } = pb.id === this.youId ? this.me : pb;
      for (const [wid, lvl] of pb.wp) {
        if (wid === 'cpi_giratoria') {
          // #FiqueEmCasa (#121): doses de vacina orbitando, mesma geometria
          // determinística do servidor. Com sprite: uma seringa por dose,
          // apontada na tangente da órbita; sem: a trilha de pontos antiga
          const eff = WEAPONS.cpi_giratoria.scale(lvl);
          const vac = fxTex('vacina');
          for (let b = 0; b < eff.beams; b++) {
            const ang = simTime * eff.rotSpeed + (b / eff.beams) * Math.PI * 2;
            if (vac) {
              const r = eff.radius + eff.beamLen * 0.5;
              const dose = this.aura(vac);
              dose.anchor.set(0.5);
              dose.scale.set(eff.beamLen / vac.width);
              dose.rotation = ang + Math.PI / 2;
              dose.position.set(pos.x + Math.cos(ang) * r, pos.y + Math.sin(ang) * r);
              continue;
            }
            for (const [frac, scale] of [[0.25, 0.7], [0.62, 0.85], [1, 1.4]] as const) {
              const r = eff.radius + eff.beamLen * frac;
              const dot = this.aura(this.auraDotTex);
              dot.anchor.set(0.5);
              dot.scale.set(scale);
              dot.position.set(pos.x + Math.cos(ang) * r, pos.y + Math.sin(ang) * r);
            }
          }
        }
        if (wid === 'cruz_credo') {
          const eff = WEAPONS.cruz_credo.scale(lvl);
          // Mesmo ângulo determinístico do servidor: cruz onde o dano acontece
          for (let b = 0; b < eff.beams; b++) {
            const ang = simTime * eff.rotSpeed + (b / eff.beams) * Math.PI * 2;
            this.drawCruzOrbiter(
              cruzTex, pos.x, pos.y, ang, eff.radius, eff.beamLen * Renderer.CRUZ_SPRITE_SCALE,
              eff.rotSpeed * 0.055, simTime + b * 0.7,
            );
          }
        }
        if (wid === 'rachadinha') {
          // ICMS: o limite mostra o raio real, o vórtice marca a origem e as
          // moedas percorrem inimigo -> jogador. Assim o efeito explica o dreno
          // sem cobrir a horda com várias cópias grandes do mesmo sprite.
          const eff = WEAPONS.rachadinha.scale(lvl);
          const pulse = 0.78 + Math.sin(simTime * 4.2) * 0.12;

          const ring = this.aura(this.icmsRingTex);
          ring.anchor.set(0.5);
          ring.scale.set(eff.radius / Renderer.ICMS_RING_R);
          ring.rotation = -simTime * 0.22;
          ring.alpha = pulse;
          ring.position.set(pos.x, pos.y);

          const tex = fxTex('icms');
          if (tex) {
            const core = this.aura(tex);
            const size = Math.min(84, eff.radius * 0.56);
            core.anchor.set(0.5);
            core.scale.set((size / tex.width) * (1 + Math.sin(simTime * 3.1) * 0.035));
            core.rotation = simTime * 1.15;
            core.alpha = 0.76;
            core.position.set(pos.x, pos.y);
          }

          let drains = 0;
          const r2 = eff.radius ** 2;
          for (const [id, , ex, ey] of snap.enemies) {
            const dx = pos.x - ex, dy = pos.y - ey;
            const d2 = dx * dx + dy * dy;
            if (d2 > r2 || d2 < 1) continue;

            // Fases derivadas do id mantêm o movimento estável entre frames.
            // O desvio perpendicular curva a trajetória para dentro do vórtice.
            const travel = (simTime * 0.9 + (id % 17) / 17) % 1;
            const dist = Math.sqrt(d2);
            const bend = Math.sin(travel * Math.PI) * 13 * (id % 2 ? 1 : -1);
            const coin = this.aura(this.icmsCoinTex);
            coin.anchor.set(0.5);
            coin.scale.set(0.68 + Math.sin(travel * Math.PI) * 0.34);
            coin.rotation = simTime * 3.5 + id;
            coin.alpha = Math.sin(travel * Math.PI) * 0.9;
            coin.position.set(
              ex + dx * travel - (dy / dist) * bend,
              ey + dy * travel + (dx / dist) * bend,
            );
            if (++drains >= Renderer.ICMS_DRAIN_TARGETS) break;
          }
        }
        // Procissão Giratória (#98/#121): dupla órbita de cruzes em rotação contrária —
        // mesma geometria determinística do servidor (OrbitWeapon)
        if (wid === 'concordata_divina') {
          const eff = EVOLVED_WEAPONS.concordata_divina.scale(); // suprema: nível único
          for (let ring = 0; ring < (eff.rings ?? 1); ring++) {
            const rr = eff.radius * (1 + ring * 0.75);
            const dir = ring % 2 ? -1 : 1;
            for (let b = 0; b < eff.beams; b++) {
              const ang = dir * simTime * eff.rotSpeed + (b / eff.beams) * Math.PI * 2;
              this.drawCruzOrbiter(
                cruzTex, pos.x, pos.y, ang, rr, eff.beamLen * Renderer.CRUZ_SPRITE_SCALE,
                dir * eff.rotSpeed * 0.055, simTime + b * 0.7 + ring,
              );
            }
          }
        }
      }
    }
    // Sprites que sobraram do frame anterior ficam invisíveis no pool
    for (let i = this.auraUsed; i < this.auraPool.length; i++) this.auraPool[i].visible = false;
  }

  // ---------------------------------------------------------------------------
  // Efeitos transitórios (eventos pontuais do servidor)
  // ---------------------------------------------------------------------------
  onEvent(ev: GameEvent): void {
    if (!this.running) return;
    if (ev.type === 'fx') this.spawnFx(ev);
    if (ev.type === 'quote') this.spawnQuote(ev);
    if (ev.type === 'bossSpawn') this.shake = Math.max(this.shake, 14);
    if (ev.type === 'casino') {
      this.spawnQuote({ text: `${ev.name} abriu a maleta!`, x: ev.x, y: ev.y });
    }
  }

  // Efeito instantâneo desenhado (spells.png): sprite que aparece e some. `anim`
  // recebe k (1 -> 0 ao longo do ttl) pra escalar/mover/fadear; o default só
  // fixa a largura e faz o fade. Retorna false se o sprite ainda não carregou.
  private spriteBurst(
    name: string, x: number, y: number, width: number,
    opts: { ttl?: number; rot?: number; anim?: (sp: Sprite, k: number) => void } = {},
  ): boolean {
    const tex = fxTex(name);
    if (!tex) return false;
    const sp = new PIXI.Sprite(tex);
    sp.anchor.set(0.5);
    sp.position.set(x, y);
    sp.rotation = opts.rot ?? 0;
    const s = width / tex.width;
    this.layers.fx.addChild(sp);
    const ttl = opts.ttl ?? 0.45;
    this.transients.push({
      ttl, max: ttl, sp,
      draw: (_, k) => {
        if (opts.anim) opts.anim(sp, k);
        else { sp.scale.set(s); sp.alpha = Math.min(k * 2.2, 1); }
      },
    });
    return true;
  }

  private spawnFx(ev: FxEvent): void {
    const mk = (draw: (g: Graphics, k: number, dt: number) => void, ttl = 0.4): void => {
      this.transients.push({ ttl, max: ttl, draw, g: this.newG() });
    };
    switch (ev.fx) {
      case 'cone': {
        const cor = ev.hostile ? 0xd42a2a : 0xffe14d;
        // O servidor sempre manda range/angle/arc no fx de cone
        const range = ev.range!, angle = ev.angle!, arc = ev.arc!;
        mk((g, k) => {
          g.clear();
          g.beginFill(cor, 0.25 * k);
          g.moveTo(ev.x, ev.y);
          g.arc(ev.x, ev.y, range, angle - arc / 2, angle + arc / 2);
          g.closePath().endFill();
        }, 0.35);
        // Sprite da arma na direção do cone (soluço do Bolsonaro / maço do
        // Mensalão). Fica de pé — nada de "HIC!" de cabeça pra baixo ao mirar
        // pra esquerda. Cones hostis (boss) seguem só no arco vermelho.
        if (!ev.hostile) {
          const name = CONE_FX[ev.kind ?? ''];
          if (name) {
            const px = ev.x + Math.cos(angle) * range * 0.45;
            const py = ev.y + Math.sin(angle) * range * 0.45;
            this.spriteBurst(name, px, py, range * 0.55, { ttl: 0.4 });
          }
        }
        break;
      }
      case 'wave': case 'nova': case 'freeze': {
        const cor = ev.fx === 'wave' ? 0xffffff : ev.fx === 'nova' ? 0xff8c42 : 0x66bbff;
        if (ev.fx === 'nova') this.shake = Math.max(this.shake, 8);
        const radius = ev.radius!; // sempre presente nesses fx
        mk((g, k) => {
          g.clear();
          g.lineStyle(6 * k + 1, cor, 0.7 * k).drawCircle(ev.x, ev.y, radius * (1 - k * 0.6));
        }, 0.45);
        // 8 de Janeiro: explosão radial no centro, crescendo; STF: martelo no
        // gelo, tamanho fixo (o anel azul já marca o raio de congelamento)
        if (ev.fx === 'nova') {
          this.spriteBurst('explosao', ev.x, ev.y, radius * 1.5, {
            ttl: 0.5,
            anim: (sp, k) => {
              const base = (radius * 1.5) / sp.texture.width;
              sp.scale.set(base * (1.15 - k * 0.2));
              sp.alpha = k;
            },
          });
        } else if (ev.fx === 'freeze') {
          this.spriteBurst('stf', ev.x, ev.y, 110, { ttl: 0.55 });
        }
        break;
      }
      case 'zoneBlast': {
        this.shake = Math.max(this.shake, ev.kind === 'cachaca' || ev.kind === 'cadeira' ? 8 : 6);
        // O estouro da cachaça já tem sprite próprio (drawZones): só treme a tela
        if (ev.kind === 'cachaca') break;
        const cor = ZONE_COLORS[ev.kind ?? ''] ?? 0xd42a2a;
        const radius = ev.radius!; // sempre presente no zoneBlast
        mk((g, k) => {
          g.clear();
          g.beginFill(cor, 0.4 * k).drawCircle(ev.x, ev.y, radius).endFill();
        }, 0.3);
        break;
      }
      case 'steal': {
        mk((g, k) => {
          g.clear();
          g.lineStyle(4, 0x8e44ad, 0.8 * k).drawCircle(ev.x, ev.y, 380 * (1 - k));
        }, 0.5);
        break;
      }
      case 'pix': {
        // Aliados Políticos: o aperto de mão por baixo do pano sobe e some no
        // ponto do repasse (substitui o antigo texto com emoji — ver CLAUDE.md)
        const ok = this.spriteBurst('aliados', ev.x, ev.y - 20, 56, {
          ttl: 0.7,
          anim: (sp, k) => {
            sp.scale.set(56 / sp.texture.width);
            sp.y = ev.y - 20 - (1 - k) * 22;
            sp.alpha = Math.min(k * 2.2, 1);
          },
        });
        if (!ok) this.spawnQuote({ text: 'PIX enviado', x: ev.x, y: ev.y - 30 });
        break;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Dano flutuante (RPG): compara o hp de cada inimigo entre snapshots, agrega
  // os ticks numa janela curta (dps não vira chuva de "1") e solta o número
  // pixelado subindo. Roda uma vez por snapshot novo, não por frame.
  // ---------------------------------------------------------------------------
  private trackDamage(snap: Snapshot): void {
    if (snap === this.lastDmgSnap) return;
    this.lastDmgSnap = snap;
    const now = performance.now();
    const seen = this.dmgSeen;
    seen.clear();
    for (const e of snap.enemies) {
      const id = e[0], hp = e[4] * (e[6] ?? 0);
      seen.add(id);
      const prev = this.enemyHp.get(id);
      if (prev !== undefined && hp < prev - 0.01) {
        let entry = this.dmgAcc.get(id);
        if (!entry) {
          entry = { acc: 0, since: now, x: 0, y: 0 };
          this.dmgAcc.set(id, entry);
        }
        entry.acc += prev - hp;
        entry.x = e[2];
        entry.y = e[3];
      }
      this.enemyHp.set(id, hp);
    }
    // Sumiu do snapshot: morreu (ou saiu do AOI). Golpe final = acumulado +
    // o hp que restava — só se o corpo estava na tela (anti-GC no wipe)
    for (const [id, hp] of this.enemyHp) {
      if (seen.has(id)) continue;
      const entry = this.dmgAcc.get(id);
      const sp = this.sprites.enemies.get(id);
      if (sp) this.spawnDmgFloat(sp.x, sp.y - 20, (entry?.acc ?? 0) + hp);
      this.enemyHp.delete(id);
      this.dmgAcc.delete(id);
    }
    // Flush periódico dos vivos: janela fechou, número na tela
    for (const [id, entry] of this.dmgAcc) {
      if (now - entry.since < Renderer.DMG_FLUSH_MS) continue;
      this.spawnDmgFloat(entry.x, entry.y - 20, entry.acc);
      this.dmgAcc.delete(id);
    }
  }

  private spawnDmgFloat(x: number, y: number, dmg: number): void {
    const v = Math.round(dmg);
    if (v < 1) return;
    if (!dmgFloatAllowed(
      x, y,
      this.cullMinX, this.cullMaxX, this.cullMinY, this.cullMaxY,
      this.dmgFloats.length, Renderer.DMG_CAP,
    )) return;
    let t = this.dmgFloatFree.pop();
    if (!t) {
      t = new PIXI.Text('', {
        fontFamily: "'VT323', 'Courier New', monospace", fontSize: 22, fontWeight: 'bold',
        fill: 0xffffff, stroke: 0x14141a, strokeThickness: 4,
      });
      t.anchor.set(0.5, 1);
      t.roundPixels = true; // pixel crocante, sem subpixel borrado
      this.disableInteractive(t as unknown as Container);
      this.layers.quotes.addChild(t);
    }
    t.text = String(v);
    t.visible = true;
    t.alpha = 1;
    const big = v >= 30; // porrada grande: número maior e mais laranja
    t.scale.set(big ? 1.4 : 1);
    t.tint = big ? 0xffb347 : 0xfff3a0;
    // Offset fixo (sem Math.random): menos alocação de estado e menos jitter visual
    t.position.set(x, y);
    this.dmgFloats.push({ sp: t, ttl: Renderer.DMG_TTL });
  }

  private updateDmgFloats(dt: number): void {
    const arr = this.dmgFloats;
    for (let i = arr.length - 1; i >= 0; i--) {
      const f = arr[i];
      f.ttl -= dt;
      if (f.ttl <= 0) {
        f.sp.visible = false;
        this.dmgFloatFree.push(f.sp);
        arr[i] = arr[arr.length - 1]; // swap-remove: ordem não importa
        arr.pop();
        continue;
      }
      f.sp.y -= 44 * dt;                                            // sobe...
      f.sp.alpha = Math.min(1, f.ttl / (Renderer.DMG_TTL * 0.45));  // ...e some
    }
  }

  // Explosão de pixels quando um inimigo morre. Sprites Texture.WHITE tintados
  // num pool + orçamento por frame (wipe de horda não estoura GC).
  private spawnDeathBurst(x: number, y: number, cor: number, big = false): void {
    // Boss sempre tenta burst (orçamento separado do flood de grunts)
    if (!big) {
      if (this.burstBudget <= 0) return;
      this.burstBudget--;
    }
    const nParts = deathBurstPartCount({
      big,
      particleAlive: this.particles.length,
      particleCap: Renderer.PARTICLE_CAP,
      burstBudgetLeft: big ? 99 : 1,
    });
    if (nParts <= 0) return;
    if (big) this.shake = Math.max(this.shake, 12);
    for (let i = 0; i < nParts; i++) {
      const a = (i / nParts) * Math.PI * 2 + (big ? 0 : i * 0.37);
      const spd = 60 + (i % 5) * (big ? 40 : 22);
      const size = big ? 3 + (i % 4) : 2 + (i % 3);
      let sp = this.particleFree.pop();
      if (!sp) {
        sp = new PIXI.Sprite(PIXI.Texture.WHITE);
        sp.anchor.set(0.5);
        this.disableInteractive(sp as unknown as Container);
        this.particleLayer.addChild(sp);
      }
      sp.visible = true;
      sp.tint = cor;
      sp.alpha = 1;
      sp.width = size;
      sp.height = size;
      sp.position.set(x, y);
      this.particles.push({ sp, vx: Math.cos(a) * spd, vy: Math.sin(a) * spd, ttl: Renderer.PARTICLE_TTL });
    }
  }

  private updateParticles(dt: number): void {
    const parts = this.particles;
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.ttl -= dt;
      if (p.ttl <= 0) {
        p.sp.visible = false;
        this.particleFree.push(p.sp);
        parts[i] = parts[parts.length - 1]; // swap-remove: ordem não importa
        parts.pop();
        continue;
      }
      p.sp.x += p.vx * dt;
      p.sp.y += p.vy * dt;
      p.sp.alpha = p.ttl / Renderer.PARTICLE_TTL;
    }
  }

  private newG(): Graphics {
    const g = new PIXI.Graphics();
    this.layers.fx.addChild(g);
    return g;
  }

  private spawnQuote(ev: QuoteSpawn): void {
    const t = new PIXI.Text(ev.text, {
      fontSize: ev.boss ? 22 : 15,
      fill: ev.boss ? 0xffd117 : 0xffffff,
      stroke: 0x000000, strokeThickness: 4,
      fontWeight: ev.boss ? '900' : 'bold',
      wordWrap: true, wordWrapWidth: 320, align: 'center',
    });
    t.anchor.set(0.5);
    t.position.set(ev.x, ev.y - 30);
    this.layers.quotes.addChild(t);
    this.transients.push({
      ttl: 2.4, max: 2.4, text: t,
      draw: (_, k) => { t.alpha = Math.min(k * 3, 1); t.y -= 0.5; },
    });
  }

  private updateTransients(dt: number): void {
    for (let i = this.transients.length - 1; i >= 0; i--) {
      const fx = this.transients[i];
      fx.ttl -= dt;
      const k = Math.max(fx.ttl / fx.max, 0);
      fx.draw(fx.g, k, dt);
      if (fx.ttl <= 0) {
        fx.g?.destroy();
        fx.text?.destroy();
        fx.sp?.destroy();
        this.transients.splice(i, 1);
      }
    }
  }

  destroy(): void {
    this.running = false;
    // SEM varredura de texture/baseTexture: os sprites usam os caches de
    // módulo compartilhados (charSheets/spriteFactory/fxSprites), construídos
    // uma vez por carga da página. A varredura os destruía junto e o Renderer
    // seguinte da REELEIÇÃO — único caminho que recria um Renderer sem
    // recarregar a página — ficava sem NENHUM sprite: só terreno e HUD
    // (issue #135). As texturas exclusivas deste Renderer morrem na lista.
    this.app.destroy(true, { children: true });
    for (const tex of this.ownTextures) tex.destroy(true);
    this.ownTextures.length = 0;
  }
}
